// Winnipeg, "Skating the frozen Forks": a January night at The Forks, light snow falling on the frozen Red River.
// The viewer stands on the river ice 20 m off the St. Boniface (east) bank, below the Taché Promenade and the
// Cathedral (49.8885 N, 97.1249 W; the water's edge is at 97.1246 W on OpenStreetMap), and looks north-west across the
// Red at The Forks (bearing about 312°; the frame spans 282° to 342°, 8 px a degree). Left to right, as seen from
// there: the south end of downtown over the Forks' bare riverbank trees (282-292°), the Canadian Museum for Human
// Rights on the Forks' bank (298°, 500 m: the glass cloud, the Tower of Hope, the limestone roots), downtown's towers
// round Portage and Main (303-311°, 1.1-1.3 km), the Esplanade Riel's west end on the Forks' bank (314°), its pylon and
// restaurant over the river (325°, 290 m), the deck running on toward the viewer to St. Boniface (its east end at
// 357°, off the frame), the Provencher Bridge just beyond it, and the Exchange District's and Point Douglas's low roofs
// further north (315-342°). The Forks Market and its tower (251°) and Union Station (274°) are left of the frame, the
// St. Boniface Cathedral (68°) behind the viewer, the CN bridge over the Red (346°) just right of it.
// The Museum and the bridge are drawn about 1.25 times their true angular size against the distance between them
// (as on the postcards); the order, the sides and what stands in front of what are as seen from the ice.
// On the ice: the Nestaweya River Trail, a plowed skating lane along the river, running from the near left away north
// under the bridges, widening by the access into a flooded oval of glare ice that mirrors the city; lanterns on posts;
// the skate rental and cocoa cabin at the access (near left, its roof sign the billboard); a warming hut on the ice
// (near right) and a fire barrel beside it; the bench where skaters lace up.
// Back to front: deep winter sky, stars, the moon -> downtown -> the Museum -> the Forks' riverbank trees -> the
// Provencher Bridge -> the Esplanade Riel, its stays and restaurant -> the frozen river, the trail and the oval's
// mirror -> lanterns, skaters -> the cabin, the hut, the fire -> the near snowbank -> snow at three depths.
//
// Real ratios (CMHR: 100 m to the tip, the cloud's roof at 50 m; Esplanade Riel: pylon 57 m over the deck, deck about
// 12 m over the winter ice; angles from the viewpoint above; photos from the St. Boniface bank on Commons):
// - CMHR: the Tower of Hope above the cloud's roof is 0.50 of the building's height. Here 58 of 115 px (0.50).
// - CMHR: height to its width as seen from the south-east, about 1.30 (photo). Here 115 to 90 px (1.28).
// - Esplanade Riel: the pylon over the deck to the deck over the ice, 57 to 12 m (4.75). Here 111 to 23 px (4.83).
// - Esplanade Riel: the pylon over the deck to the west span as seen from here, 11.2° to 10.4° (1.07). Here 111 to
//   104 px (1.07).
// - The Museum's tip to the pylon's tip, as seen from here: 12.0° to 13.6° (0.88). Here 124 to 134 px over the ice
//   line (0.93).
import { W, H, LOOP, TAU, frac, clamp, lerp, smooth, wave, step, env, cycle, hash, rng, bayer, hex,
  makeCanvas, ditherFill, makeHalo, drawHalo, glyph, FONT3, streak, makePitch, gradeAd } from '../engine/core.js';
import { weatherPlans, drawWeather } from '../engine/weather.js';

let ctx;                                          // this scene's 480x270 context, set by create()
const halo = (hl, x, y, a = 1) => drawHalo(ctx, hl, x, y, a);

/* ================= Layout ================= */
const HZ = 156;                                   // the far ice line at the foot of the Forks' bank
const MOON = { x: 418, y: 46, r: 7 };
// the Canadian Museum for Human Rights (Predock, 2014): x span of the building, the Tower of Hope's tip and column,
// the cloud's roof at the tower's foot, the base on the Forks' bank (hidden by the riverbank trees)
const MU = { x0: 84, x1: 173, tip: 32, roof: 90, base: 147, tx: 124 };
// the Esplanade Riel (2004): the pylon's column and tip, the west end on the Forks' bank; the deck rises a little
// toward the viewer (its east end is nearer)
const BR = { px: 341, tip: 22, x0: 237 };
const deckY = x => Math.round(133 + (BR.px - x) * 0.04);      // 137 at the west end, 133 at the pylon
const ANCH = { top: 55, low: 104 };                // the stays' anchorages on the pylon (0.70 to 0.26 of its height over the deck)
const CABIN = { x0: 4, x1: 92, roof: 154, base: 200 };        // the skate rental and cocoa cabin, near left
const BB = { x: 22, y: 122, w: 52, h: 20 };         // the cabin's roof sign: the billboard's face
const HUT = { x0: 392, x1: 444, base: 204, top: 150 };        // the warming hut on the ice, near right
const BARREL = { x: 379, y: 204 };                  // the fire barrel beside it
const BENCH = { x0: 452, x1: 476, y: 207 };         // the bench where skaters lace up
// a person's height by the row of their feet: 22 px at row 200, so the near row stands above the bottom 63 rows
const personH = y => Math.round(0.46 * (y - 152));
// by the access the trail widens into a cleared oval of glare ice, flooded smooth; it softly mirrors the city about
// the horizon (row 2 * HZ - y)
const OVAL = { cx: 228, cy: 204, rx: 146, ry: 15 };
const ovalHalf = x => OVAL.ry * Math.sqrt(Math.max(0, 1 - ((x - OVAL.cx) / OVAL.rx) ** 2));
const ovalSpan = y => { const q = 1 - ((y - OVAL.cy) / OVAL.ry) ** 2; if (q <= 0) return null; const h = OVAL.rx * Math.sqrt(q); return [Math.round(OVAL.cx - h), Math.round(OVAL.cx + h)]; };
const ovalTop = x => Math.round(OVAL.cy - ovalHalf(x));
const inOval = (x, y) => { const s = ovalSpan(y); return !!s && x >= s[0] && x <= s[1]; };
// the Nestaweya River Trail north from the oval's far rim, under the bridges and away up the river (ordered by x)
const TRAIL = [[318, 192], [346, 184], [370, 175], [388, 167], [400, 161], [408, 157]];
const trailHalf = y => Math.max(1, (y - HZ) * 0.2);
const RINK = { x0: 108, x1: 178, y0: 169, y1: 182 };          // a shinny patch on the far ice, two little nets
const FAR_HUT = { x: 266, base: 171 };                        // another warming hut far up the ice

/* ================= Pixel helpers ================= */
function painter(w, h) {
  const [c, x] = makeCanvas(w, h), img = x.createImageData(w, h), d = img.data, cache = {};
  const put = (i, y, col, a = 255) => {
    i = Math.round(i); y = Math.round(y);
    if (i < 0 || y < 0 || i >= w || y >= h || !col) return;
    const rgb = cache[col] || (cache[col] = hex(col)), o = (y * w + i) * 4;
    if (a >= 255 || !d[o + 3]) { d[o] = rgb[0]; d[o + 1] = rgb[1]; d[o + 2] = rgb[2]; d[o + 3] = a; return; }
    const f = a / 255;                                      // blend over what is already there
    d[o] = Math.round(lerp(d[o], rgb[0], f)); d[o + 1] = Math.round(lerp(d[o + 1], rgb[1], f)); d[o + 2] = Math.round(lerp(d[o + 2], rgb[2], f));
    d[o + 3] = Math.max(d[o + 3], a);
  };
  const get = (i, y) => (i < 0 || y < 0 || i >= w || y >= h) ? 0 : d[(y * w + i) * 4 + 3];
  const fill = (x0, y0, ww, hh, col) => { for (let y = y0; y < y0 + hh; y++) for (let i = x0; i < x0 + ww; i++) put(i, y, col); };
  const line = (xa, ya, xb, yb, col, a = 255) => {          // a 1 px line, one pixel per step along the longer axis
    const n = Math.max(Math.abs(xb - xa), Math.abs(yb - ya), 1);
    for (let k = 0; k <= n; k++) put(lerp(xa, xb, k / n), lerp(ya, yb, k / n), col, a);
  };
  return { c, x, put, get, fill, line, done: () => { x.putImageData(img, 0, 0); return c; } };
}
function mixHex(a, b, t) {
  const A = hex(a), B = hex(b), f = k => Math.round(lerp(A[k], B[k], t)).toString(16).padStart(2, '0');
  return `#${f(0)}${f(1)}${f(2)}`;
}
function ramp(pal, v, i, y) {
  const f = clamp(v, 0, 1) * (pal.length - 1);
  let k = Math.floor(f); if (f - k > bayer(i, y)) k++;
  return pal[Math.min(k, pal.length - 1)];
}
const R = (x, y, w, h, c) => { ctx.fillStyle = c; ctx.fillRect(x, y, w, h); };
function vnoise(x, y, sc, seed) {                     // smooth value noise for the snow's drifts
  const gx = x / sc, gy = y / sc, x0 = Math.floor(gx), y0 = Math.floor(gy), fx = gx - x0, fy = gy - y0;
  const s = v => v * v * (3 - 2 * v), h = (i, j) => hash(i, j, seed);
  return lerp(lerp(h(x0, y0), h(x0 + 1, y0), s(fx)), lerp(h(x0, y0 + 1), h(x0 + 1, y0 + 1), s(fx)), s(fy));
}

/* ================= Sign font: FONT3 with wide M, W, N, K, V ================= */
// The 3 px K and W read as H at 1x and the N as Π.
const WIDE = {
  M: ['10001', '11011', '10101', '10001', '10001'], W: ['10001', '10001', '10101', '10101', '01010'],
  V: ['10001', '10001', '01010', '01010', '00100'], N: ['1001', '1101', '1011', '1001', '1001'],
  K: ['1001', '1010', '1100', '1010', '1001'],
};
const glyphOf = ch => WIDE[ch] ?? FONT3[ch];
const signWidth = s => [...s.toUpperCase()].reduce((w, ch) => w + (glyphOf(ch)?.[0].length ?? 3) + 1, 0) - (s ? 1 : 0);
function sign(c, s, x, y, col) {
  let cx = Math.round(x);
  for (const ch of s.toUpperCase()) { const g = glyphOf(ch); if (g) glyph(c, g, cx, Math.round(y), col); cx += (g?.[0].length ?? 3) + 1; }
  return cx - x - 1;
}

/* ================= Sky: deep winter blue, the city's glow low over the Forks ================= */
const SKY = ['#03051a', '#050820', '#070b28', '#0a0f30', '#0e1438', '#121a40', '#171f48', '#1d2550', '#252b58',
  '#2e325e', '#383862', '#433e66', '#4f4568', '#5c4c68'];
function renderSky() {
  return ditherFill(W, H, SKY, (i, y) => {
    let v = Math.pow(Math.min(1, y / HZ), 1.7) * 0.8;
    v += Math.max(0, 1 - Math.hypot((i - 250) / 190, (y - 150) / 60)) * 0.16;   // downtown's glow on the haze
    v += Math.max(0, 1 - Math.hypot((i - MOON.x) / 70, (y - MOON.y) / 50)) * 0.1;   // moonlit sky round the moon
    return v;
  });
}
function renderMoon() {
  const r = MOON.r, s = r * 2 + 1, p = painter(s, s);
  const maria = [[-2, -2, 1.7], [2, 1, 1.5], [1, -3, 1], [-2, 3, 1], [3, -1, 0.8]];
  for (let y = 0; y < s; y++) for (let i = 0; i < s; i++) {
    const dx = i - r, dy = y - r, dd = Math.hypot(dx, dy);
    if (dd > r + 0.35) continue;
    let col = dx + dy < -5 ? '#fdfcf4' : '#eeeee4';
    if (dd > r - 1.1) col = dx + dy > 0 ? '#bcbcb4' : '#dedcd0';
    for (const [cx, cy, cr] of maria) if (Math.hypot(dx - cx, dy - cy) < cr) col = dd > r - 1.1 ? '#b0b0a8' : '#d0cec4';
    p.put(i, y, col);
  }
  return p.done();
}
const STARS = [];
{
  const r = rng(204);
  while (STARS.length < 260) {
    const x = Math.floor(r() * W), y = 2 + Math.floor(r() * 110);
    if (Math.hypot(x - MOON.x, y - MOON.y) < 16) continue;
    STARS.push({ x, y, k: 10 + Math.floor(r() * 50), ph: r(), b: y < 60 ? 0.45 + r() * 0.5 : 0.2 + r() * 0.3, big: r() < 0.06 });
  }
}
// thin snow cloud drifting east, moonlit on top, no outlines, dithered into the sky (tileable)
const WISPS = [{ seed: 3, y: 6, h: 20, k: 2 }, { seed: 8, y: 30, h: 22, k: 3 }, { seed: 13, y: 58, h: 22, k: 2 }, { seed: 19, y: 86, h: 22, k: 3 },
  { seed: 23, y: 18, h: 20, k: 1 }, { seed: 29, y: 44, h: 20, k: 2 }, { seed: 37, y: 72, h: 20, k: 1 }, { seed: 41, y: 100, h: 22, k: 2 }];
function renderWisp(def) {
  const r = rng(def.seed), h = def.h, blobs = [];
  for (let b = 0; b < 3; b++) {
    const cx = r() * W, len = 40 + r() * 60;
    for (let k = 0; k < 7; k++) { const u = (k / 6 - 0.5); blobs.push({ x: cx + u * len + (r() - 0.5) * 8, y: h * 0.62 - (1 - Math.abs(u) * 2) * (2 + r() * 3), rx: 7 + r() * 9, ry: 2.5 + r() * 2.5 }); }
  }
  const p = painter(W, h);
  const sky = SKY[Math.round(Math.pow(Math.min(1, (def.y + h / 2) / HZ), 1.7) * 0.8 * (SKY.length - 1))];
  const tone = [0.13, 0.19, 0.27].map(a => mixHex(sky, '#9aa4d8', a));
  for (let y = 0; y < h; y++) for (let x = 0; x < W; x++) {
    let v = 0;
    for (const b of blobs) { let dx = x - b.x; dx -= Math.round(dx / W) * W; v = Math.max(v, 1 - (dx * dx) / (b.rx * b.rx) - ((y - b.y) ** 2) / (b.ry * b.ry)); }
    if (v <= 0) continue;
    let up = 0;
    for (const b of blobs) { let dx = x - b.x; dx -= Math.round(dx / W) * W; up = Math.max(up, 1 - (dx * dx) / (b.rx * b.rx) - ((y - 2 - b.y) ** 2) / (b.ry * b.ry)); }
    if (v > 0.35) p.put(x, y, up <= 0.1 ? tone[2] : v > 0.7 ? tone[1] : tone[0]);
    else if ((v / 0.35) > bayer(x, y)) p.put(x, y, tone[0]);
  }
  return { c: p.done(), y: def.y, k: def.k };
}

/* ================= Downtown: the towers round Portage and Main, the Exchange District's warehouses ================= */
const WARMW = ['#ffe2a4', '#f8c672', '#e8a456'];
const NIGHT = '#141a3c';
// [x centre, width, top row, body colour, crown]; behind the Museum and the riverbank trees, their feet hidden
const TOWERS = [
  [8, 16, 112, '#232a4e', 'flat'], [28, 12, 121, '#1e2546', 'flat'], [48, 16, 126, '#262c4c', 'flat'], [72, 12, 114, '#20284a', 'slim'],
  [170, 14, 92, '#1c2348', 'band'], [188, 10, 106, '#232a4c', 'flat'], [212, 16, 90, '#212850', 'peak'],
  [231, 14, 97, '#1f264a', 'flat'], [250, 10, 112, '#232a4c', 'blue'], [268, 12, 120, '#262c4e', 'flat'],
];
const WARE = [[280, 22, 134], [302, 18, 131], [320, 24, 136], [346, 20, 133], [366, 26, 138], [392, 18, 141], [410, 24, 140],
  [434, 20, 143], [454, 26, 142]];   // the Exchange District and Point Douglas: [x0, width, top]
const WINS = [];                                   // windows that switch on and off, filled in by renderCity
function renderCity() {
  const p = painter(W, H), put = p.put, r = rng(31);
  for (const [cx, w, top, col, crown] of TOWERS) {
    const x0 = cx - (w >> 1);
    for (let y = top; y < 152; y++) for (let i = x0; i < x0 + w; i++) {
      const edge = i === x0 ? mixHex(col, '#6a70a0', 0.3) : i === x0 + w - 1 ? mixHex(col, NIGHT, 0.4) : col;
      put(i, y, edge);
    }
    if (crown === 'peak') {                                          // a lit gable crown over the office floors
      for (let k = 0; k < 8; k++) for (let i = cx - k; i <= cx + k - 1; i++) put(i, top - 8 + k, i === cx - k || i === cx + k - 1 ? '#fff8e0' : mixHex('#f4f0e4', col, 0.35 + k / 16));
      for (let i = x0; i < x0 + w; i++) put(i, top, '#e8e4f0');
    }
    if (crown === 'band') for (let i = x0; i < x0 + w; i++) { put(i, top + 1, '#d8dcef'); put(i, top + 2, '#8a90b8'); }
    if (crown === 'blue') for (let y = top; y < top + 4; y++) for (let i = x0; i < x0 + w; i++) put(i, y, y === top ? '#8aa4ff' : '#3a52d0');
    if (crown === 'slim') { put(cx, top - 1, '#ff4a4a'); for (let i = x0; i < x0 + w; i++) put(i, top, '#c8ccdc'); }
    for (let y = top + 3; y < 150; y += 3) for (let i = x0 + 1; i < x0 + w - 1; i += 2) {   // window grid, a share lit
      const v = r();
      if (v < 0.36) put(i, y, v < 0.1 ? WARMW[0] : WARMW[1]);
      else if (v < 0.5) WINS.push({ x: i, y, n: 6 + Math.floor(r() * 10), ph: r(), id: WINS.length });
      else if (v < 0.6) put(i, y, '#3a4270');
    }
  }
  for (const [x0, w, top] of WARE) {                                // the Exchange District: brick warehouses, cornices
    for (let y = top; y < 152; y++) for (let i = x0; i < x0 + w; i++) put(i, y, y === top ? '#4a3e52' : '#2c2640');
    for (let i = x0; i < x0 + w; i++) put(i, top, '#cfd4ee');        // snow on the flat roofs
    for (let y = top + 3; y < 150; y += 3) for (let i = x0 + 2; i < x0 + w - 2; i += 3) { const v = r(); if (v < 0.4) put(i, y, v < 0.15 ? WARMW[0] : WARMW[2]); }
  }
  for (let x = 0; x < W; x++) { put(x, 146, '#1a1e36'); put(x, 147, '#22284a'); if (x % 24 === 0) for (let y = 148; y < 152; y++) put(x, y, '#1a1e36'); }   // the rail viaduct
  return p.done();
}

/* ================= The Canadian Museum for Human Rights: the Tower of Hope, the glass cloud, the limestone roots =================
   The Tower of Hope is lit from inside, warm white and gold, brightest in its upper lantern (CMHR at night, Commons); the
   cloud is dark glass in long horizontal wings that sweep down to the west, warm rooms behind it; the Tyndall
   limestone roots are floodlit honey-tan. */
const GOLD = { hi: '#fff6e0', lt: '#ffe4a8', md: '#f0c070', sh: '#c8964e', dk: '#8a6840', mul: '#a87a44' };
const GLASS = { hi: '#a8c4c4', lt: '#6e9496', md: '#466a76', sh: '#2c4658', dk: '#1c2e42', deep: '#0e1828', floor: '#5a8486' };
const STONE = { hi: '#ecd4a4', lt: '#d4b47e', md: '#b08e62', sh: '#806650', dk: '#54463a' };
// the Tower of Hope: a near-straight column of stepped glass, its set-backs on alternating sides like a crystal, a slim
// notched crown and a short spire: [left, right] spans per row (photos from the east and south-east, r3-r6)
function towerSpans(y) {
  if (y < 37) return [[126, 126]];                    // the spire
  if (y < 39) return [[125, 127]];
  if (y < 42) return [[123, 123], [125, 127]];        // the crown's notch
  if (y < 48) return [[122, 128]];                    // the crown
  if (y < 62) return [[120, 129]];                    // set back on the west
  if (y < 74) return [[120, 131]];                    // stepped out on the east
  if (y < 84) return [[117, 131]];                    // stepped out on the west
  return [[117, 133]];                                // the foot, stepped out on the east
}
const STEPS = [42, 48, 62, 74, 84];                    // the rows where the column steps out (their sills catch the light)
// the cloud: a wide, low shell of glass bands, each overhanging the one below and sweeping out and down to the west:
// [top row, bottom row, west end at the top, west end at the bottom, east end]; under a near-flat roof by the tower
const BANDS = [[90, 101, 101, 95, 141], [101, 113, 98, 88, 145], [113, 126, 92, 84, 149], [126, 147, 90, 89, 151]];
const bandWest = ([y0, y1, xa, xb], y) => Math.round(lerp(xa, xb, Math.pow((y - y0) / (y1 - y0), 0.55)));
// the Tyndall limestone roots on the east: angular blocks with slanted tops, [x0, x1, top at x0, top at x1]
const ROOTS = [[133, 146, 92, 95], [146, 160, 98, 104], [158, 173, 107, 113]];
const rootTop = ([x0, x1, a, b], x) => Math.round(lerp(a, b, (x - x0) / (x1 - x0)));
function renderMuseum() {
  const p = painter(W, H), put = p.put, r = rng(52);
  // the glass bands: a lit top edge, floors glowing in rows, mullions, the dark underside where one overhangs the next
  BANDS.forEach((bd, k) => {
    const [y0, y1, , , xe] = bd;
    for (let y = y0; y < Math.min(y1, MU.base); y++) {
      const xw = bandWest(bd, y), d = y - y0, last = y === y1 - 1;
      for (let x = xw + (d === 0 ? 2 : 0); x <= xe; x++) {
        const near = Math.max(0, 1 - Math.abs(x - MU.tx) / 22);
        let col = ramp([GLASS.deep, GLASS.dk, GLASS.sh], 0.75 - d / (y1 - y0) * 0.5 - k * 0.08, x, y);
        if (d > 1 && d % 3 === 1) col = (x + y) % 11 < 8 ? GLASS.floor : GLASS.md;     // a floor's glow behind the glass
        if ((x - xw) % 6 === 5 && d > 0) col = GLASS.dk;                                  // the mullions
        if (x - xw < 2 && d > 0) col = GLASS.lt;                                          // the band's swept west edge
        if (d === 0) col = near > 0.25 ? mixHex(GLASS.hi, GOLD.md, near * 0.6) : GLASS.hi;
        else if (d === 1 && k < 3) col = near > 0.25 ? mixHex(GLASS.lt, GOLD.sh, near * 0.4) : GLASS.lt;
        if (last && k < 3) col = GLASS.deep;
        put(x, y, col);
      }
    }
    for (let n = 0; n < 8; n++) {                                          // warm rooms
      const y = y0 + 2 + Math.floor(r() * Math.max(1, y1 - y0 - 4)), x = bandWest(bd, y) + 4 + Math.floor(r() * (xe - bandWest(bd, y) - 8));
      if (y < MU.base - 2) put(x, y, r() < 0.5 ? '#f8d088' : '#e8b060');
    }
  });
  // the limestone roots: lit honey-tan faces, their slanted tops catching the light, joints and a slit or two
  for (const rt of ROOTS) for (let x = rt[0]; x <= rt[1]; x++) {
    const top = rootTop(rt, x);
    for (let y = top; y < MU.base; y++) {
      const fx = (x - rt[0]) / (rt[1] - rt[0]);
      let col = y === top ? STONE.hi : fx < 0.12 ? STONE.lt : fx > 0.85 ? STONE.sh : ramp([STONE.md, STONE.lt], 0.5 + (y - top) / 50, x, y);
      if ((y - rt[2]) % 10 === 9 && fx >= 0.12 && y > top + 2) col = STONE.md;
      put(x, y, col);
    }
  }
  // the Tower of Hope: warm white-gold glass, brightest at the crown, on a lattice of mullions; lit sills at its steps
  for (let y = MU.tip; y < MU.roof + 1; y++) for (const [L, Rr] of towerSpans(y)) for (let x = L; x <= Rr; x++) {
    const v = clamp(1 - (y - 40) / 62, 0, 1);                             // brighter toward the top
    let col = ramp([GOLD.sh, GOLD.md, GOLD.lt, GOLD.hi], v * 0.9 + (x === L ? 0.15 : 0), x, y);
    if ((x - 117) % 3 === 2 && x > L && x < Rr) col = mixHex(col, GOLD.mul, 0.55);        // vertical mullions
    if ((y - 32) % 6 === 5 && x > L && x < Rr) col = mixHex(col, GOLD.mul, 0.4);          // floor lines
    if (STEPS.includes(y) && !towerSpans(y - 1).some(([a, b]) => x >= a && x <= b)) col = GOLD.hi;   // the step's lit sill, where it juts out
    if (x === Rr && Rr > L) col = GOLD.dk;
    put(x, y, col);
  }
  return p.done();
}

/* ================= The Forks' riverbank: bare elms and cottonwoods, rimed with frost, the riverwalk's lamps ================= */
const SNOW = ['#eef2ff', '#ccd4f0', '#a4acd0', '#7c84aa', '#5a6290'];
const RIME = [];                                   // frost on the branches that glints as the lights catch it
function renderBank() {
  const p = painter(W, H), put = p.put, r = rng(77);
  // the bank's snowy slope down to the ice, west of the river (left) and the far north reach under the bridges (right)
  for (let x = 0; x < W; x++) {
    const top = x < BR.x0 + 5 ? 146 + Math.round(Math.sin(x * 0.07) * 1.5) : 150 + Math.round((x - BR.x0) / 120);
    for (let y = top; y < HZ; y++) put(x, y, ramp([SNOW[4], SNOW[3], SNOW[2]], 0.3 + (y - top) / 18 + hash(x, y, 3) * 0.15, x, y));
    put(x, HZ, '#4a5280');
  }
  // bare trees: trunks, forking branches, the rime picking out their upper sides; denser in front of the Museum's foot
  const trees = [];
  for (let x = -6; x < BR.x0 + 5; x += (x > 70 && x < 180 ? 5 : 7) + Math.floor(r() * 5)) trees.push({ x, h: (x > 70 && x < 200 ? 21 : 12) + Math.floor(r() * 8), base: 149 });
  for (let x = BR.x0 + 9; x < W + 6; x += 9 + Math.floor(r() * 8)) trees.push({ x, h: 5 + Math.floor(r() * 5), base: 152 });
  for (const tr of trees) {
    const top = tr.base - tr.h;
    for (let y = top + 3; y <= tr.base; y++) put(tr.x, y, '#2a2c48');
    for (let k = 0; k < 18; k++) {                                // branches: short lines fanning up from the trunk
      const y0 = top + 3 + Math.floor(r() * tr.h * 0.5), len = 3 + r() * tr.h * 0.4, ang = -Math.PI / 2 + (r() - 0.5) * 1.9;
      for (let s = 0; s < len; s++) {
        const bx = tr.x + Math.cos(ang) * s, by = y0 + Math.sin(ang) * s;
        const rime = s > len * 0.5 && hash(Math.round(bx), Math.round(by), 9) < 0.55;
        put(bx, by, rime ? '#c4cce8' : '#34385a');
        if (rime && hash(Math.round(bx), Math.round(by), 10) < 0.3) RIME.push({ x: Math.round(bx), y: Math.round(by) });
      }
    }
  }
  return p.done();
}

/* ================= The bridges: the Provencher Bridge behind, the Esplanade Riel in front =================
   The Esplanade Riel's pylon is white, floodlit warm white from its foot; its 2 x 11 stays are pale; the restaurant's
   tent roof at the pylon glows; shepherd's-crook lamps along the deck (photos at night, Commons). */
const LEFT_STAYS = Array.from({ length: 11 }, (_, k) => ({ a: lerp(ANCH.top, ANCH.low, k / 10), x: Math.round(lerp(BR.x0 + 7, 328, k / 10)) }));
const RIGHT_STAYS = Array.from({ length: 11 }, (_, k) => ({ a: lerp(ANCH.top, ANCH.low, k / 10), x: Math.round(lerp(540, 355, Math.pow(k / 10, 0.9))) }));
const DECK_LAMPS = [250, 272, 296, 380, 400, 422, 444, 466];
const PROV_LAMPS = [254, 280, 306, 360, 384, 408, 432, 456, 478];
const PYLON = y => y < 40 ? 0 : y < 70 ? 1 : y < 96 ? 2 : 3;        // the pylon's half-width per row (a needle widening down)
function renderBridges() {
  const p = painter(W, H), put = p.put;
  // the Provencher Bridge: a dark deck just beyond, its lamps, its piers in the ice
  for (let x = BR.x0 + 4; x < W; x++) {
    const y = deckY(x) + 3;
    put(x, y, '#5a6088'); put(x, y + 1, '#262a48'); put(x, y + 2, '#1c2040');
  }
  for (const lx of PROV_LAMPS) for (let y = deckY(lx) - 4; y < deckY(lx) + 3; y++) put(lx, y, '#3a3e5c');
  for (const px0 of [284, 392, 452]) for (let y = deckY(px0) + 6; y < HZ + 1; y++) for (let i = 0; i < 4; i++) put(px0 + i, y, i ? '#2a2e4c' : '#4a5078');
  // the Esplanade Riel's deck: a lit parapet, the slab's face, the dark underside; its piers
  for (let x = BR.x0; x < W; x++) {
    const y = deckY(x);
    put(x, y - 2, '#b8bcd4'); put(x, y - 1, '#4a4e70');
    put(x, y, '#e4e6f0'); put(x, y + 1, '#6a6e90'); put(x, y + 2, '#22264a');
  }
  for (let y = deckY(BR.x0) - 2; y < 148; y++) for (let x = BR.x0 - 6; x < BR.x0 + 2; x++) put(x, y, x === BR.x0 - 6 ? '#8a8eaa' : '#3a3e5c');
  for (const [px0, w] of [[280, 3], [BR.px - 4, 9], [416, 4]]) for (let y = deckY(px0) + 3; y < HZ + 1; y++) for (let i = 0; i < w; i++)
    put(px0 + i, y, i === 0 ? '#9a9eb8' : i === w - 1 ? '#2a2e4c' : '#5a5f80');
  for (const lx of DECK_LAMPS) {                                       // shepherd's-crook lamps on the parapet
    const y = deckY(lx);
    for (let k = 3; k < 9; k++) put(lx, y - k, '#8a8eaa');
    put(lx + 1, y - 9, '#8a8eaa'); put(lx + 2, y - 8, '#fff0c8');
  }
  // the stays: pale lines from the pylon's anchorages down to the deck, west and east
  for (const s of LEFT_STAYS) p.line(BR.px - 1, s.a, s.x, deckY(s.x) - 2, '#e6e6f2', 170);
  for (const s of RIGHT_STAYS) p.line(BR.px + 1, s.a, s.x, deckY(Math.min(s.x, W)) - 2, '#e6e6f2', 170);
  // the pylon: a white needle, floodlit from its foot, shaded on its right
  for (let y = BR.tip; y < 128; y++) {
    const hw = PYLON(y), lit = (y - BR.tip) / (128 - BR.tip);
    for (let i = -hw; i <= hw; i++) put(BR.px + i, y, i === hw && hw > 0 ? '#b4b6cc' : i > 0 ? mixHex('#e8e8f0', '#fff6e0', lit) : lit > 0.5 ? '#fffaf0' : '#f4f4fa');
  }
  // the restaurant: a white tent roof round the pylon, lit warm from under its eave, the glazed room below
  for (let y = 112; y < 128; y++) {
    const hw = 3 + Math.round((y - 112) * 20 / 15);
    for (let i = -hw; i <= hw; i++) {
      const edge = Math.abs(i) >= hw - 1;
      put(BR.px + i, y, y >= 126 ? '#ffe2a8' : edge ? '#c8c8dc' : i < 0 ? '#f8f6f0' : '#dcdae4');
    }
  }
  for (let y = 128; y < deckY(BR.px) - 1; y++) for (let i = -20; i <= 20; i++) put(BR.px + i, y, (i + 20) % 4 === 0 ? '#8a6a48' : '#ffd890');
  return p.done();
}

/* ================= The frozen Red: snow on the ice, the cleared oval, the trail north, the shinny patch, the berm ================= */
const trailAt = x => {                                // the trail's centre row at column x, or null off it
  for (let k = 1; k < TRAIL.length; k++) if (x >= TRAIL[k - 1][0] && x <= TRAIL[k][0]) {
    const [xa, ya] = TRAIL[k - 1], [xb, yb] = TRAIL[k];
    return lerp(ya, yb, (x - xa) / (xb - xa));
  }
  return null;
};
// warm light on the snow: the cabin's hatch, the warming hut, the fire barrel (x, y, rx, ry, strength)
const POOLS = [[52, 201, 82, 24, 0.9], [418, 205, 52, 15, 0.8], [BARREL.x, BARREL.y + 1, 34, 11, 0.75]];
const warmAt = (x, y) => POOLS.reduce((m, [px0, py, rx, ry, s]) => Math.max(m, s * (1 - Math.hypot((x - px0) / rx, (y - py) / ry))), 0);
const COOL = ['#2c335e', '#363e6a', '#424a78', '#4e5886', '#5c6694', '#6c76a4', '#7e88b4', '#929cc4'];
const WARMSNOW = ['#4a3e58', '#6a5462', '#8c6c6c', '#b08a7c', '#d4aa8c', '#f0c8a0'];
const ICE = ['#4a5688', '#525e94', '#5c68a0', '#6672aa', '#7280b4'];
const ICE_WARM = ['#5a5478', '#76667e', '#927a84', '#ae9088'];
// lanterns on posts along the oval's far rim; their light lies on the ice in front of them
const LANTERNS = [114, 176, 250, 316].map(x => { const y = ovalTop(x) - 2; return { x, y, h: Math.round((y - 152) * 0.75) }; });
const SPRUCES = [];
// the plowed berm along the frame's foot: the snow the trail crews pushed off the oval, lumpy, lit on top
const bermTop = x => Math.round(246 + vnoise(x, 0, 40, 31) * 9 + vnoise(x, 3, 11, 32) * 3);
const inRink = (x, y) => x >= RINK.x0 && x <= RINK.x1 && y >= RINK.y0 && y <= RINK.y1
  && !((x < RINK.x0 + 2 || x > RINK.x1 - 2) && (y < RINK.y0 + 2 || y > RINK.y1 - 2));
function renderIce() {
  const p = painter(W, H), put = p.put, r = rng(91);
  for (let y = HZ + 1; y < H; y++) for (let x = 0; x < W; x++) {
    const d = (y - HZ) / (H - HZ), n = vnoise(x, y * 3, 22, 5) * 0.5 + vnoise(x, y * 2, 7, 6) * 0.25;
    let v = 0.22 + d * 0.5 + (n - 0.37) * 0.5;
    const w = warmAt(x, y);
    if (inOval(x, y) || inRink(x, y)) {                    // cleared ice: pale, scratched by blades, snow dust at the rim
      const rink = !inOval(x, y), e = rink ? 0.5 : 1 - Math.hypot((x - OVAL.cx) / OVAL.rx, (y - OVAL.cy) / OVAL.ry);
      const arc = hash(Math.round(y * 0.9), Math.floor((x + (y - OVAL.cy) ** 2 * 0.9) / 5), 22) < 0.14;
      const frost = e < 0.14 && hash(x, y, 23) < (0.14 - e) * 5;
      put(x, y, frost ? '#a8b2dc' : arc ? (hash(x, y, 24) < 0.5 ? '#9aa6d4' : '#8490c4')
        : w > 0.15 ? ramp(ICE_WARM, w * 1.2, x, y) : ramp(ICE, 0.25 + e * 0.3 + n * 0.3 + d * 0.3, x, y));
      continue;
    }
    if (inOval(x, y - 2) || inOval(x, y + 2) || inOval(x - 3, y) || inOval(x + 3, y) || inRink(x, y - 1) || inRink(x, y + 1) || inRink(x - 2, y) || inRink(x + 2, y)) v += 0.38;
    const cy = trailAt(x);
    if (cy !== null) {
      const hw = trailHalf(cy), off = Math.abs(y - cy);
      if (off <= hw) {                                      // the lane: smooth grey-blue ice, skate marks along it
        const mark = hash(Math.round(y * 1.3), Math.floor((x + y * 2.2) / (6 + d * 30)), 21) < 0.08;
        put(x, y, mark ? '#8a96c4' : ramp(['#4a568a', '#525e94', '#5c68a0'], 0.4 + d * 0.6 - off / hw * 0.35, x, y));
        continue;
      }
      if (off <= hw + 1 + d * 4) v += 0.32 - (off - hw) * 0.03;   // snow plowed up along both edges
    }
    put(x, y, w > 0.08 ? ramp(WARMSNOW, w * 0.9 + v * 0.3, x, y) : ramp(COOL, v, x, y));
  }
  // footprints in the snow round the access: boot pairs wandering between the cabin, the oval and the fire
  for (const [xa, ya, xb, yb] of [[96, 206, 150, 226], [300, 222, 372, 208], [20, 206, 70, 238], [380, 214, 450, 236]]) for (let k = 0; k < 9; k++) {
    const x = Math.round(lerp(xa, xb, k / 8)), y = Math.round(lerp(ya, yb, k / 8)) + (k & 1);
    if (!inOval(x, y)) { put(x, y, '#3a4272'); put(x + 1, y, '#46508a'); }
  }
  // the rink's two little nets (red frames, white mesh)
  for (const nx of [RINK.x0 + 3, RINK.x1 - 6]) { for (let i = 0; i < 4; i++) { put(nx + i, 173, '#d84a3a'); put(nx + i, 175, '#c8cce0'); } put(nx, 174, '#d84a3a'); put(nx + 3, 174, '#d84a3a'); }
  // spruce markers along the trail's edges and round the oval: irregular spacing, sizes and leans, a few gaps
  for (let x = TRAIL[0][0] + 4; x < TRAIL[TRAIL.length - 1][0]; x += 7 + Math.floor(r() * 12)) {
    const cy = trailAt(x);
    for (const side of [-1, 1]) {
      if (r() < 0.3) continue;
      const y = Math.round(cy + side * (trailHalf(cy) + 2)), h = Math.max(3, Math.round((y - HZ) * (0.14 + r() * 0.08)));
      SPRUCES.push({ x: x + Math.round((r() - 0.5) * 3), y, h, lean: r() < 0.3 ? (r() < 0.5 ? -1 : 1) : 0 });
    }
  }
  for (const x of [OVAL.cx - OVAL.rx + 14, 120, 166, 214, 262, 300, OVAL.cx + OVAL.rx - 22]) {
    if (r() < 0.25) continue;
    const y = Math.round(OVAL.cy + ovalHalf(x)) + 3 + Math.floor(r() * 3), h = Math.round((y - 152) * (0.14 + r() * 0.07));
    SPRUCES.push({ x: x + Math.round((r() - 0.5) * 8), y, h, lean: r() < 0.3 ? (r() < 0.5 ? -1 : 1) : 0 });
  }
  for (const L of LANTERNS) {                              // the lantern posts: dark timber, an iron box on top
    for (let y = L.y - L.h; y <= L.y; y++) { put(L.x, y, '#2e2430'); if (y > L.y - L.h + 4) put(L.x + 1, y, '#1e1822'); }
    for (let i = -1; i <= 2; i++) { put(L.x + i, L.y - L.h - 1, '#1e1822'); put(L.x + i, L.y - L.h + 3, '#1e1822'); }
    put(L.x - 1, L.y + 1, SNOW[1]); put(L.x + 2, L.y + 1, SNOW[1]);
  }
  // the far hut: a small timber A-frame up the ice, its door lit
  { const { x, base } = FAR_HUT;
    for (let k = 0; k < 10; k++) for (let i = -Math.round(k * 0.6); i <= Math.round(k * 0.6); i++) put(x + i, base - 10 + k, k === 0 ? SNOW[0] : i < 0 ? '#7a5638' : '#4e3426');
    put(x, base - 3, '#ffc870'); put(x, base - 2, '#ffc870'); put(x, base - 1, '#ffb050'); }
  // the berm: piled snow, its lit crest, chunks thrown up by the plow, the shadow at its foot
  for (let x = 0; x < W; x++) {
    const top = bermTop(x), w = Math.max(warmAt(x, top - 12) * 1.3, 0);
    for (let y = top; y < H; y++) {
      const dd = y - top;
      let col = dd === 0 ? SNOW[0] : dd === 1 ? SNOW[1] : ramp([COOL[3], COOL[4], COOL[5], COOL[6], SNOW[2]], 0.95 - dd / 22 + vnoise(x, y, 4, 33) * 0.2, x, y);
      if (w > 0.15 && dd < 6) col = ramp(WARMSNOW, w + 0.3 - dd * 0.06, x, y);
      put(x, y, col);
    }
    if (bermTop(x - 1) > top + 1 || bermTop(x + 1) > top + 1) put(x, top, SNOW[0]);
  }
  for (let k = 0; k < 70; k++) {                               // the chunks: lumps with lit tops and shaded bellies
    const cx = Math.floor(r() * W), cy = bermTop(cx) + 3 + Math.floor(r() * 16), rx = 2 + Math.floor(r() * 3), ry = 1 + Math.floor(r() * 2);
    for (let j = -ry; j <= ry; j++) for (let i = -rx; i <= rx; i++) if ((i / rx) ** 2 + (j / ry) ** 2 <= 1) put(cx + i, cy + j, j < 0 ? SNOW[1] : j === 0 ? SNOW[2] : COOL[4]);
  }
  // left on the berm: a shovel stuck in, a hockey stick leaning, a kid's red plastic sled
  { const sx = 306, sy = bermTop(306); for (let y = sy - 14; y < sy + 2; y++) put(sx, y, '#8a6440'); put(sx - 1, sy - 15, '#5a4030'); put(sx + 1, sy - 15, '#5a4030'); for (let y = sy + 2; y < sy + 6; y++) for (let i = -2; i <= 2; i++) put(sx + i, y, '#6a7080'); }
  { const hx = 150, hy = bermTop(150); for (let k = 0; k < 14; k++) put(hx + Math.round(k * 0.3), hy - 13 + k, '#b88a50'); for (let i = 0; i < 4; i++) put(hx + 4 + i, hy + 1, '#2a2a30'); }
  { const kx = 226, ky = bermTop(226); for (let j = 0; j < 4; j++) for (let i = 0; i < 9; i++) put(kx + i - Math.round(j * 0.5), ky - 4 + j, j === 0 ? '#ff6a5a' : i === 0 ? '#8a1e1e' : '#d8382c'); }
  return p.done();
}
function drawSpruce(put, x, y, h, lean = 0) {          // a small spruce: dark tiers, snow on their upper edges
  for (let k = 0; k < h; k++) {
    const hw = Math.round((k / h) * h * 0.32), dx = Math.round(lean * (h - k) / h);
    for (let i = -hw; i <= hw; i++) put(x + i + dx, y - h + k, k % 3 === 0 && i < 0 ? '#d8e0f6' : (k + i) % 4 === 0 ? '#2a5a4a' : '#1e4038');
  }
  put(x, y, '#3a2a24');
}
// the glare ice's soft mirror: each row of the oval shows, dimly, the row as far above the horizon as it lies below
const OVAL_ROWS = [];
for (let y = OVAL.cy - OVAL.ry + 2; y < OVAL.cy + OVAL.ry - 1; y++) { const s = ovalSpan(y); if (s) OVAL_ROWS.push({ y, x0: Math.max(0, s[0] + 4), x1: Math.min(W - 1, s[1] - 4), src: 2 * HZ - y }); }
function drawMirror(t) {
  for (const r of OVAL_ROWS) {
    const e = (r.y - (OVAL.cy - OVAL.ry)) / (2 * OVAL.ry), off = Math.round(wave(t, 6, r.y * 0.07) * 0.6);
    ctx.globalAlpha = 0.24 - e * 0.12;
    ctx.drawImage(MIRROR_SRC, r.x0, r.src, r.x1 - r.x0, 1, r.x0 + off, r.y, r.x1 - r.x0, 1);
  }
  ctx.globalAlpha = 1;
}

/* ================= The warming hut: an hourglass of twisting timber fins, warm light between them =================
   In the spirit of the Warming Huts competition's pieces on the river trail: an invented shape, not a copy of one.
   Open to the sky at its flared crown, an arched door, a stove's light inside. */
const hutHalf = u => u < 0.3 ? 11 + 8 * (1 - u / 0.3) ** 2 : 11 + 15 * ((u - 0.3) / 0.7) ** 1.4;
function renderHut() {
  const p = painter(W, H), put = p.put, { x0, x1, base, top } = HUT, cx = (x0 + x1) >> 1, Ht = base - top;
  for (let y = top; y <= base; y++) {
    const u = (y - top) / Ht, hw = hutHalf(u);
    for (let x = Math.round(cx - hw); x <= Math.round(cx + hw); x++) {
      const a = (x - cx) / hw, f = frac(a * 4.2 + u * 3.4 + 0.5);
      const dy = base - y, door = Math.abs(x - cx) <= 5 && dy < 18 && (dy < 13 || (x - cx) ** 2 + (dy - 13) ** 2 <= 25);
      let col;
      if (door) col = dy < 2 ? '#8a5a30' : dy < 8 && Math.abs(x - cx) < 4 ? '#ffd890' : '#ffbe68';
      else if (f < 0.18 && u > 0.04) col = hash(x, y, 4) < 0.7 ? '#ffc870' : '#e89a48';
      else col = ramp(['#4a3024', '#6e4a32', '#946840', '#b88c58'], 0.55 - a * 0.42 + (f > 0.82 ? -0.3 : f < 0.32 ? 0.18 : 0), x, y);
      if (!door && Math.abs(a) > 0.92) col = a < 0 ? '#c89a64' : '#3a2620';
      put(x, y, col);
    }
  }
  const hw0 = Math.round(hutHalf(0));
  for (let x = cx - hw0; x <= cx + hw0; x++) { put(x, top - 1, SNOW[0]); if (Math.abs(x - cx) < hw0 - 2) put(x, top, '#ffd890'); }
  for (let x = cx - 28; x <= cx + 28; x++) { const h = Math.max(0, 3 - Math.floor(Math.abs(x - cx) / 10)) + (hash(x, 9) < 0.4 ? 1 : 0); for (let y = base - h + 1; y <= base + 1; y++) if (!(Math.abs(x - cx) <= 5 && y <= base)) put(x, y, y === base - h + 1 ? SNOW[0] : SNOW[1]); }
  return p.done();
}

/* ================= The near left: the skate rental and cocoa cabin at the trail access, its roof sign ================= */
function renderCabin(withSign) {
  const p = painter(W, H), put = p.put, { x0, x1, roof, base } = CABIN;
  // the roof: snow-laden, eaves overhanging
  for (let y = roof - 10; y < roof; y++) {
    const inset = Math.round((roof - y) * 1.6);
    for (let x = x0 - 3 + inset; x <= x1 + 3 - inset; x++) put(x, y, y < roof - 7 ? SNOW[0] : y < roof - 2 ? (x & 1 ? SNOW[1] : SNOW[0]) : '#3a2a2a');
  }
  for (let x = x0 - 3; x <= x1 + 3; x++) { put(x, roof - 1, '#c8d0ec'); if (hash(x, 3, 1) < 0.4) put(x, roof, '#e8eeff'); }
  // log walls: dark red-brown timbers, the hatch lit gold, a door, a small window
  for (let y = roof; y <= base; y++) for (let x = x0; x <= x1; x++) {
    const log = (y - roof) % 4;
    put(x, y, log === 3 ? '#3a1e1c' : log === 0 ? '#9a4a3a' : '#7a3428');
  }
  for (let y = roof + 12; y < roof + 32; y++) for (let x = x0 + 30; x < x0 + 70; x++) put(x, y, y < roof + 14 ? '#ffe8b0' : '#ffcf80');   // the hatch
  for (let x = x0 + 28; x < x0 + 72; x++) { put(x, roof + 32, '#c8a070'); put(x, roof + 33, '#6a4630'); }                             // its counter
  for (let y = roof + 18; y <= base; y++) for (let x = x0 + 76; x < x0 + 90; x++) put(x, y, x === x0 + 76 || x === x0 + 89 ? '#3a1e1c' : '#5a2c22');   // door
  put(x0 + 79, roof + 36, '#e8c060');
  for (let y = roof + 14; y < roof + 24; y++) for (let x = x0 + 6; x < x0 + 22; x++) put(x, y, (x - x0 - 6) % 8 === 7 || (y - roof - 14) % 5 === 4 ? '#3a1e1c' : '#ffd890');
  for (let y = roof - 17; y < roof - 6; y++) { put(x0 + 80, y, '#2a2a30'); put(x0 + 81, y, '#4a4a54'); put(x0 + 82, y, '#2a2a30'); }   // the stovepipe
  for (let i = 79; i <= 83; i++) put(x0 + i, roof - 18, SNOW[1]);
  // the skate rack on the wall beside the window: pairs of hanging skates
  for (let i = x0 + 4; i < x0 + 25; i++) put(i, roof + 27, '#3a2a24');                     // a rail with rental skates hanging
  for (let k = 0; k < 4; k++) { const sx = x0 + 5 + k * 5; put(sx + 1, roof + 28, '#8a8a94'); put(sx, roof + 29, '#2a2a34'); put(sx + 1, roof + 29, '#2a2a34'); put(sx, roof + 30, '#2a2a34'); put(sx + 1, roof + 30, '#3a3a44'); put(sx + 2, roof + 30, '#3a3a44'); for (let i = -1; i < 4; i++) put(sx + i, roof + 31, '#d8dce8'); }
  // snow piled against the walls
  for (let x = x0 - 4; x <= x1 + 5; x++) { const h = 2 + Math.round(vnoise(x, 0, 6, 8) * 4); for (let y = base - h; y <= base + 1; y++) put(x, y, y === base - h ? SNOW[0] : SNOW[1]); }
  if (withSign) {                                              // the roof sign's frame and posts, two gooseneck lamps
    for (const lx of [BB.x + 6, BB.x + BB.w - 7]) for (let y = BB.y + BB.h; y < roof - 8; y++) put(lx, y, '#2a1e1e');
    for (let x = BB.x - 1; x <= BB.x + BB.w; x++) { put(x, BB.y - 1, '#2a1e1e'); put(x, BB.y + BB.h, '#2a1e1e'); }
    for (let y = BB.y - 1; y <= BB.y + BB.h; y++) { put(BB.x - 1, y, '#2a1e1e'); put(BB.x + BB.w, y, '#2a1e1e'); }
    for (const lx of [BB.x + 12, BB.x + BB.w - 13]) { put(lx, BB.y - 3, '#3a3a44'); put(lx, BB.y - 2, '#3a3a44'); put(lx + 1, BB.y - 4, '#3a3a44'); put(lx + 2, BB.y - 4, '#fff0c8'); }
  }
  return p.done();
}
function renderBillboardAd() {                                // bannock and hot cocoa, on red-painted boards
  const [c, x] = makeCanvas(BB.w, BB.h);
  const back = ditherFill(BB.w, BB.h, ['#5a1a1e', '#7a2626', '#8e3030'], (i, j) => 0.9 - j / BB.h * 0.6);
  x.drawImage(back, 0, 0);
  const l1 = 'BANNOCK', l2 = 'HOT COCOA';
  sign(x, l1, Math.round((BB.w - signWidth(l1)) / 2), 3, '#fff0d0');
  sign(x, l2, Math.round((BB.w - signWidth(l2)) / 2), 11, '#f8c060');
  return gradeAd(c, { level: 0.92, tint: '#141a3c', mix: 0.06, wear: 0.3, seed: 7 });
}

/* ================= The near right: the fire barrel, a stack of firewood, the bench ================= */
function renderNearRight() {
  const p = painter(W, H), put = p.put;
  const { x0, x1, y } = BENCH;                                 // the bench: two snowy planks on dark legs
  for (let i = x0; i <= x1; i++) { put(i, y - 8, SNOW[1]); put(i, y - 7, '#8a6440'); put(i, y - 6, '#5a3e28'); put(i, y - 3, SNOW[0]); put(i, y - 2, '#9a7248'); put(i, y - 1, '#5a3e28'); }
  for (const lx of [x0 + 2, x1 - 2]) for (let yy = y - 8; yy <= y; yy++) put(lx, yy, '#22222c');
  const { x: bx, y: by } = BARREL;                             // the fire barrel: a rusty drum, holes glowing
  for (let yy = by - 11; yy <= by; yy++) for (let i = -5; i <= 5; i++) put(bx + i, yy, yy === by - 11 ? '#ff9a40' : (yy - by) % 4 === 0 ? '#2a1e1e' : i < -2 ? '#7a4a30' : i > 2 ? '#3a2420' : '#5a3424');
  for (const [hx, hy] of [[bx - 2, by - 6], [bx + 1, by - 3], [bx - 3, by - 2]]) put(hx, hy, '#ff8a30');
  for (let k = 0; k < 4; k++) for (let i = 0; i < 9 - k * 2; i++) put(HUT.x1 + 2 + i + k, HUT.base - k * 2, (i + k) % 3 === 0 ? '#c8a070' : '#6a4630');   // split logs by the hut
  for (let i = -6; i < 6; i++) put(bx + i, by + 1, SNOW[1]);
  return p.done();
}

/* ================= People: parkas, toques, skates; lit by the cabin, the hut and the fire ================= */
const SKINS = ['#f2c8a0', '#e0aa80', '#c08a60', '#8a5a3c'], HAIRS = ['#1c1412', '#3c2418', '#6c4222', '#d8b878', '#8e3e1c', '#c8c4c0'];
const PNIGHT = '#141a3a', GLOW = '#ffd8a0';
function look(h, skin, hair, top, bot, o = {}) {
  const dk = o.dk ?? 0.2, m = c => mixHex(c, PNIGHT, dk);
  const hh = h >= 20 ? 5 : h >= 14 ? 4 : 3, th = Math.round(h * (o.coat ? 0.5 : 0.42));
  return { h, hh, hw: h >= 19 ? 4 : 3, th, tw: h >= 21 ? 7 : h >= 16 ? 6 : 5, ll: h - hh - th,
    skin: m(SKINS[skin]), hair: m(HAIRS[hair]), hairL: mixHex(HAIRS[hair], GLOW, 0.3), top: m(top), topD: mixHex(top, PNIGHT, dk + 0.28),
    topL: mixHex(top, GLOW, 0.3), bot: m(bot), shoe: m('#1e1a20'), long: o.long || 0,
    scarf: o.scarf ? m(o.scarf) : null, toque: o.toque ? m(o.toque) : null, pom: o.pom ? m(o.pom) : '#e8e2d4', skates: !!o.skates };
}
/**
 * One person, feet on row fy, centred on column x; returns the row of the top of the head.
 * o.view: 0 from behind, 1 facing us, 2 in profile (o.dir 1 right, -1 left); o.step: stride frame; o.sit: seated at fy;
 * o.lean: head and body lean (skaters); o.aL / o.aR: 0 down, 3 out, 4 mug raised, 5 hand to the face
 */
function person(x, fy, P, o = {}) {
  x = Math.round(x); fy = Math.round(fy);
  const { hw, hh, tw, th } = P, view = o.view || 0, dir = o.dir || 1, side = view === 2, ln = o.lean || 0;
  const legs = o.sit ? 0 : P.ll;
  const ty = fy - legs - th + 1, hy = ty - hh, bw = side ? tw - 2 : tw, tx = x - (bw >> 1);
  const blade = '#d8dce8';
  if (legs) {
    const ly = fy - legs + 1;
    if (side) {
      if (o.step) {                                    // the push: the back leg out behind, the front leg bent under
        R(x - dir * 2 - (dir > 0 ? 1 : 0), ly + 1, 2, legs - 1, P.bot); R(x - dir * 4 - (dir > 0 ? 1 : 0), fy - 1, 3, 1, P.shoe);
        R(x + (dir > 0 ? 0 : -1), ly, 2, legs, P.bot); R(dir > 0 ? x : x - 2, fy, 3, 1, P.shoe);
        if (P.skates) { R(x - dir * 4 - (dir > 0 ? 2 : -1), fy, 4, 1, blade); R(dir > 0 ? x - 1 : x - 2, fy + 1, 4, 1, blade); }
      } else {
        R(x - 1, ly, 2, legs, P.bot); R(dir > 0 ? x - 1 : x - 2, fy, 3, 1, P.shoe);
        if (P.skates) R(x - 2, fy + 1, 5, 1, blade);
      }
    } else {
      const lw = bw >= 7 ? 3 : 2;
      R(tx, ly, lw, legs, P.bot); R(tx + bw - lw, ly, lw, legs, P.bot);
      R(tx, fy, lw, 1, P.shoe); R(tx + bw - lw, fy, lw, 1, P.shoe);
      if (P.skates) { R(tx, fy + 1, lw + 1, 1, blade); R(tx + bw - lw, fy + 1, lw + 1, 1, blade); }
    }
  } else if (o.sit) {
    R(tx - 1, fy - 1, bw + 2, 2, P.bot);
    if (view === 1) { R(tx, fy + 1, 2, 5, P.bot); R(tx + bw - 2, fy + 1, 2, 5, P.bot); R(tx, fy + 6, 2, 1, P.shoe); R(tx + bw - 2, fy + 6, 2, 1, P.shoe); }
  }
  const bx = tx + (side ? dir * ln : 0);
  R(bx, ty, bw, th, P.top);
  if (!side) R(bx + bw - 1, ty + 1, 1, th - 1, P.topD);
  R(bx, ty, bw, 1, P.topL);
  if (P.scarf) R(bx, ty, bw, 2, P.scarf);
  const arm = (ax, st, sg) => {
    if (!st) { R(ax, ty + 1, 1, th - 2, P.topD); R(ax, ty + th - 1, 1, 1, P.skin); }
    else if (st === 3) { R(sg < 0 ? ax - 2 : ax, ty + 2, 3, 1, P.topD); R(sg < 0 ? ax - 3 : ax + 3, ty + 2, 1, 1, P.skin); }
    else if (st === 4) { R(ax, ty, 1, 3, P.topD); R(ax, ty - 1, 1, 1, P.skin); R(ax + (sg < 0 ? -1 : 0), ty - 3, 2, 2, '#f0e8d8'); }
    else if (st === 6) { R(ax, hy - 1, 1, ty - hy + 2, P.topD); R(ax, hy - 2, 1, 1, P.skin); }
    else { R(ax, ty, 1, 2, P.topD); R(ax + (sg < 0 ? 1 : -1), hy + hh - 2, 1, 2, P.skin); }
  };
  if (side) {
    const st = o.aR || 0;
    if (st === 3) { R(dir > 0 ? bx + bw : bx - 3, ty + 2, 3, 1, P.topD); R(dir > 0 ? bx + bw + 3 : bx - 4, ty + 2, 1, 1, P.skin); }
    else if (st === 4) { R(x + dir, ty, 1, 3, P.topD); R(x + dir * 2, ty - 2, 2, 2, '#f0e8d8'); }
    else if (st === 6) { R(x + dir, hy - 1, 1, ty - hy + 2, P.topD); R(x + dir, hy - 2, 1, 1, P.skin); }
    else { const ax = x + (o.step ? -dir * 2 : 0); R(ax, ty + 1, 1, th - 2, P.topD); R(ax - (o.step ? dir : 0), ty + th - 2, 1, 1, P.skin); }
  } else { arm(tx - 1, o.aL, -1); arm(tx + bw, o.aR, 1); }
  const hx = x - (hw >> 1) + (side ? dir * ln * 2 : 0), hr = hh >= 5 ? 2 : 1;
  if (view === 0) {
    R(hx, hy, hw, hh, P.hair); R(hx, hy, hw, 1, P.hairL);
    if (P.long) R(hx, hy + hh, hw, P.long, P.hair); else R(hx + 1, hy + hh - 1, hw - 2, 1, P.skin);
  } else if (view === 1) {
    R(hx, hy, hw, hh, P.skin); R(hx, hy, hw, hr, P.hair);
    if (P.long) { R(hx - 1, hy + 1, 1, hh + P.long - 1, P.hair); R(hx + hw, hy + 1, 1, hh + P.long - 1, P.hair); }
  } else { R(hx, hy, hw, hh, P.skin); R(hx, hy, hw, hr, P.hair); R(dir > 0 ? hx : hx + hw - 1, hy, 1, hh - 1 + P.long, P.hair); }
  if (P.toque) { R(hx, hy - 1, hw, 2, P.toque); R(hx + (hw >> 1), hy - 2, 1, 1, P.pom); }
  return hy;
}
/* ================= Skaters: in profile, leaning into the stride =================
   frame 1 pushes off (the free leg out behind with its blade on the ice, the arms swinging), frame 0 glides (the free
   skate lifted behind); arms 'back' clasps the hands behind the back like a long-distance skater */
function skater(x, fy, P, dir, frame, arms = 'swing') {
  x = Math.round(x); fy = Math.round(fy);
  const { hh, hw, th } = P, legs = P.ll - 1, lean = Math.max(1, Math.round(th * 0.3));
  const hip = fy - legs, ty = hip - th + 1, hy = ty - hh, bw = Math.max(3, P.tw - 2), blade = '#d8dce8', back = mixHex(P.bot, PNIGHT, 0.3);
  for (let j = 0; j < legs; j++) R(x + (j > legs / 2 ? dir : 0) - (dir > 0 ? 1 : 0), hip + j, 2, 1, P.bot);   // the support leg, knee bent
  R(x - 1 - (dir > 0 ? 0 : 1), fy, 3, 1, P.shoe); R(x - 2, fy + 1, 5, 1, blade);
  if (frame) {                                                       // the push: the free leg out behind, its blade down
    for (let j = 0; j <= legs; j++) R(x - dir * Math.round(j * 0.8) - (dir > 0 ? 1 : 0), hip + j, 2, 1, back);
    const fx = x - dir * Math.round(legs * 0.8);
    R(fx - 1, fy, 3, 1, P.shoe); R(fx - 2, fy + 1, 4, 1, blade);
  } else {                                                           // the glide: the free skate lifted behind
    for (let j = 0; j < legs - 2; j++) R(x - dir * (2 + (j > legs / 2 ? 1 : 0)) - (dir > 0 ? 1 : 0), hip + j, 2, 1, back);
    R(x - dir * 4 - 1, fy - 2, 3, 1, P.shoe); R(x - dir * 4 - 2, fy - 1, 4, 1, blade);
  }
  for (let j = 0; j < th; j++) R(x - (bw >> 1) + dir * Math.round((th - j) * lean / th), ty + j, bw, 1, j === 0 ? P.topL : P.top);
  if (P.scarf) R(x - (bw >> 1) + dir * lean, ty, bw, 2, P.scarf);
  const sx = x + dir * lean, sy = ty + 1;
  if (arms === 'back') {
    for (let j = 0; j < th - 2; j++) R(x - dir * (bw >> 1) + dir * Math.round((th - j) * lean / th), sy + j, 1, 1, P.topD);
    R(x - dir * ((bw >> 1) + 1) - (dir > 0 ? 0 : 1), ty + th - 3, 2, 1, P.skin);
  } else {
    const fw = frame ? 1 : -1;
    for (let j = 0; j < th - 2; j++) { R(sx - dir * fw * Math.round(j * 0.6), sy + j, 1, 1, mixHex(P.topD, PNIGHT, 0.25)); R(sx + dir * fw * Math.round(j * 0.6), sy + j, 1, 1, P.topD); }
    R(sx + dir * fw * Math.round((th - 2) * 0.6), sy + th - 2, 1, 1, P.skin);
  }
  const hx = x - (hw >> 1) + dir * (lean + 1);
  R(hx, hy, hw, hh, P.skin); R(hx, hy, hw, hh >= 5 ? 2 : 1, P.hair); R(dir > 0 ? hx : hx + hw - 1, hy, 1, hh - 1 + P.long, P.hair);
  if (P.toque) { R(hx, hy - 1, hw, 2, P.toque); R(hx + (hw >> 1), hy - 2, 1, 1, P.pom); }
  return hy;
}
const tiny = (x, fy, h, col, st) => {                  // a far figure under 9 px: a coat, a head, two legs
  x = Math.round(x); fy = Math.round(fy);
  const legs = Math.max(1, Math.round(h * 0.35)), body = Math.max(1, h - legs - 1);
  R(x, fy - legs - body, 2, body, col); R(x, fy - h, 2, 1, '#e8c8a8');
  R(x - (st ? 1 : 0), fy - legs + 1, 1, legs, '#20202c'); R(x + 1 + (st ? 1 : 0), fy - legs + 1, 1, legs, '#20202c');
};
const sized = (base, h) => {                          // a look scaled to height h, keeping its colours
  const P = look(h, 0, 0, '#000000', '#000000');
  return Object.assign(P, { skin: base.skin, hair: base.hair, hairL: base.hairL, top: base.top, topD: base.topD, topL: base.topL,
    bot: base.bot, shoe: base.shoe, toque: base.toque, pom: base.pom, scarf: h > 16 ? base.scarf : null, skates: base.skates, long: h > 18 ? base.long : 0 });
};

/* ================= The loop's timeline (seconds) =================
   t          what
   all loop   skaters loop the oval and skate the trail north under the bridges; shinny on the far patch; cars cross
              the Provencher Bridge and walkers the Esplanade Riel; windows switch on and off; the lanterns and the fire
              flicker; smoke from the cabin's stovepipe and the hut's crown; breath steams; snow blows over the ice
   8-58       a parent pulls a child on a sled along the oval's far rim and away up the trail (back 168-218)
   20-52      a passenger train eastbound on the rail viaduct behind the Forks
   30, 90, 150, 210   a goal on the shinny patch: the puck slides in, sticks go up
   40-64      the Tower of Hope's light climbs from its foot to the lantern, which flares (58) and settles
   70-112     a dog walker comes down the trail and along the oval's far rim to the left (back 186-226)
   92-140     a skater glides in from the trail to the middle of the oval and spins slowly (104-124); the people at the
              fire turn to watch and clap (124-128); she skates off up the trail
   120-130    the Cathedral's bells behind the viewer (120, 123.5, 127): pigeons fly up from it over the river (121-138)
   146-196    a freight train westbound on the viaduct, its headlight first
   156-206    light pillars rise over downtown in the cold air and fade                                           */
const SLED_T = [[8, 58, 1], [168, 218, -1]];
const DOG_T = [[70, 112, -1], [186, 226, 1]];
const GOALS = [30, 90, 150, 210];
const CLIMB = { a: 40, top: 56, b: 64 };
const SPIN = { a: 92, at: 104, b: 124, off: 140 };
const BELLS = [120, 123.5, 127], PIGEONS = { a: 121, b: 138 };
const TRAINS = [{ a: 20, b: 52, dir: 1, freight: false, len: 96 }, { a: 146, b: 196, dir: -1, freight: true, len: 250 }];
const PILLARS = { a: 156, b: 206, f: 12, xs: [[166, 92], [212, 108], [238, 84], [300, 96], [362, 78], [436, 70]] };
// the far rim path (behind the lanterns) and on up the trail: walkers, the sled, the dog
const PATH = [[-24, 194], [60, 192], [140, 190], [228, 188], [300, 189], [332, 186], [356, 179], [378, 171], [396, 163], [408, 158]];
const PATH_LEN = PATH.slice(1).reduce((L, p, k) => L + Math.hypot(p[0] - PATH[k][0], p[1] - PATH[k][1]), 0);
function pathPoint(u) {
  let d = clamp(u, 0, 1) * PATH_LEN;
  for (let k = 1; k < PATH.length; k++) {
    const [xa, ya] = PATH[k - 1], [xb, yb] = PATH[k], L = Math.hypot(xb - xa, yb - ya);
    if (d <= L) return [lerp(xa, xb, d / L), lerp(ya, yb, d / L)];
    d -= L;
  }
  return PATH[PATH.length - 1];
}
const trailPoint = u => { const x = lerp(TRAIL[0][0], TRAIL[TRAIL.length - 1][0], u); return [x, trailAt(x)]; };

/* ================= The cast =================
   rank: drawn while rank < crowd (Settings, People); rank 0 is there at every level above 0 (the cocoa vendor and his
   customer, a skater on the oval, one at the fire, the spinner); pairs and families share one rank */
const TOPS = ['#8a2a34', '#2a4a7a', '#c8a050', '#3a6a5a', '#5a3a6a', '#b0503a', '#4a4a58', '#d8c8a8', '#2a5a8a', '#7a5a3a'];
const BOTS = ['#22202c', '#33466e', '#3a3a48', '#4a3a30'];
const rk = i => frac((i + 0.5) * 0.618);
const STAY = [
  { id: 'buyer', x: 50, y: 203, P: look(23, 1, 2, TOPS[8], BOTS[0], { coat: true, toque: '#c84a3a', scarf: '#e8c040' }), o: { view: 0 }, rank: 0, breath: [52, 182] },
  { id: 'sipper', x: 72, y: 204, P: look(24, 0, 4, TOPS[7], BOTS[1], { coat: true, toque: '#2a3a5a', long: 3 }), o: { view: 2, dir: 1 }, rank: rk(3), breath: [77, 183] },
  { id: 'warm', x: 367, y: 204, P: look(23, 0, 3, TOPS[0], BOTS[1], { coat: true, toque: '#3a6a5a', long: 3 }), o: { view: 2, dir: 1, aR: 3 }, rank: 0, breath: [371, 184] },
  { id: 'mug', x: 393, y: 205, P: look(24, 2, 0, TOPS[3], BOTS[0], { coat: true, toque: '#e8e0d0', pom: '#c84a3a' }), o: { view: 2, dir: -1 }, rank: rk(5), breath: [388, 184] },
  { id: 'facing', x: 381, y: 197, P: look(20, 3, 1, TOPS[5], BOTS[2], { coat: true, toque: '#2a2a3a', dk: 0.3 }), o: { view: 1, aL: 3, aR: 3 }, rank: rk(1), breath: [381, 178] },
  { id: 'lacer', x: 465, y: BENCH.y - 3, P: look(24, 1, 1, TOPS[9], BOTS[2], { coat: true, toque: '#e8c040', skates: true }), o: { view: 1, sit: true }, rank: rk(0), breath: [465, 184] },
];
const P_VENDOR = look(22, 1, 3, '#2a5a4a', BOTS[0], { toque: '#c84a3a', dk: 0.05 });
const OVAL_SKATERS = [
  { ph: 0.05, n: 8, arms: 'swing', P: look(24, 0, 2, TOPS[1], BOTS[0], { coat: true, toque: '#d84a6a', skates: true, scarf: '#e8e0d0' }), rank: 0 },
  { ph: 0.55, n: 8, arms: 'back', P: look(25, 3, 0, TOPS[2], BOTS[1], { coat: true, toque: '#2a2a3a', skates: true }), rank: rk(6) },
  { ph: 0.3, n: 6, arms: 'swing', P: look(24, 1, 4, TOPS[4], BOTS[0], { coat: true, toque: '#e8c040', skates: true, long: 3 }), rank: rk(2) },
  { ph: 0.8, n: 6, arms: 'swing', P: look(16, 0, 3, '#d84a6a', BOTS[1], { coat: true, toque: '#3a8ad8', skates: true }), rank: rk(8), kid: true },
];
const TRAIL_SKATERS = [
  { ph: 0.1, n: 4, P: look(24, 0, 3, TOPS[6], BOTS[2], { coat: true, toque: '#c8d0ec', skates: true }), rank: rk(10) },
  { ph: 0.45, n: 3, P: look(24, 2, 1, TOPS[9], BOTS[0], { coat: true, toque: '#3a6a5a', skates: true }), rank: rk(4) },
  { ph: 0.7, n: 5, P: look(24, 1, 2, TOPS[0], BOTS[1], { coat: true, toque: '#e8e0d0', skates: true }), rank: rk(11) },
  { ph: 0.92, n: 4, P: look(24, 0, 5, TOPS[1], BOTS[0], { coat: true, toque: '#c84a3a', skates: true }), rank: rk(7) },
];
const SHINNY = [{ cx: 128, k: 7, ph: 0.1, col: '#c83a3a' }, { cx: 146, k: 9, ph: 0.6, col: '#2a5aa8' }, { cx: 158, k: 8, ph: 0.35, col: '#c83a3a' }, { cx: 136, k: 11, ph: 0.8, col: '#2a5aa8' }];
const SHINNY_P = SHINNY.map((s, i) => look(12, i & 3, i % 6, s.col, '#22202c', { toque: i & 1 ? '#e8e0d0' : '#2a2a3a', skates: true, dk: 0.3 }));
const P_SPIN = look(24, 0, 3, '#e8e0f0', '#3a3a58', { coat: false, toque: '#8a3aa8', skates: true, scarf: '#8a3aa8', dk: 0.12 });
const P_PARENT = look(24, 0, 2, TOPS[8], BOTS[0], { coat: true, toque: '#e8c040' }), P_KID = look(13, 0, 3, '#d84a6a', BOTS[1], { toque: '#3a8ad8', dk: 0.15 });
const P_DOGGER = look(24, 2, 0, TOPS[4], BOTS[0], { toque: '#3a6a5a', coat: true });
const walkAt = (list, t) => {
  const w = list.find(([a, b]) => t >= a && t < b);
  if (!w) return null;
  const u = (t - w[0]) / (w[1] - w[0]);
  return { u: w[2] > 0 ? u : 1 - u, dir: w[2] };
};
const shadow = (x, fy, w) => { ctx.globalAlpha = 0.4; R(Math.round(x) - (w >> 1), Math.round(fy) + 1, w, 1, '#141832'); ctx.globalAlpha = 1; };
function breath(x, y, t, ph) {                          // a puff of breath in the cold every few seconds
  const { u } = cycle(t, 60, ph);
  if (u < 0.35) halo(HL.breath, x + u * 8, y - u * 6, 0.6 * (1 - u / 0.35));
}
function reflectOf(x, y, draw) {                          // a dim mirror image on the cleared ice, clipped to it
  const X = Math.round(x), Y = Math.round(y), sp = ovalSpan(Y + 2);
  if (!sp) return;
  const bottom = Math.round(OVAL.cy + ovalHalf(X));
  ctx.save(); ctx.beginPath(); ctx.rect(sp[0], Y + 2, sp[1] - sp[0], Math.max(0, bottom - Y - 2)); ctx.clip();
  ctx.globalAlpha = 0.22; ctx.translate(0, 2 * Y + 3); ctx.scale(1, -1); draw(); ctx.restore(); ctx.globalAlpha = 1;
}
function drawSled(t, DL) {
  const w = walkAt(SLED_T, t);
  if (!w) return;
  const [x, y] = pathPoint(w.u), d = w.dir, h = personH(y), k = h / 24, st = step(t, 768) & 1;
  if (h < 7) return;
  const a = Math.min(1, w.u * 30, (1 - w.u) * 30, d > 0 ? 1 : 1);
  DL.push([y, () => {
    ctx.globalAlpha = a;
    shadow(x, y, Math.round(8 * k));
    const hy = person(x, y, sized(P_PARENT, h), { view: 2, dir: d, step: st });
    const sx = Math.round(x - d * 16 * k);                                 // the toboggan on its rope, the child on it
    ctx.fillStyle = '#9a96a8';
    for (let q = 3; q < 13 * k; q += 2) ctx.fillRect(Math.round(x - d * q), Math.round(hy + 13 * k + q * 0.25), 1, 1);
    const L = Math.max(5, Math.round(13 * k));
    R(sx - (L >> 1), y - 1, L, 1, '#c88a4a'); R(sx - (L >> 1), y, L, 1, '#5a3420'); R(sx + d * (L >> 1), y - 3, 1, 3, '#c88a4a');
    if (h > 12) person(sx - d, y - 2, sized(P_KID, Math.round(13 * k)), { view: 2, dir: d, sit: true });
    ctx.globalAlpha = 1;
  }]);
}
function drawDog(t, DL) {
  const w = walkAt(DOG_T, t);
  if (!w) return;
  const [x, y] = pathPoint(w.u), d = w.dir, h = personH(y), k = h / 24, st = step(t, 768) & 1;
  if (h < 7) return;
  DL.push([y, () => {
    shadow(x, y, Math.round(8 * k));
    person(x, y, sized(P_DOGGER, h), { view: 2, dir: d, step: st });
    const dx = Math.round(x + d * 14 * k), dy = Math.round(y);                // the lead, a black dog trotting ahead
    ctx.fillStyle = '#5a4a3a';
    for (let q = 3; q < 13 * k; q += 2) ctx.fillRect(Math.round(x + d * q), Math.round(y - 11 * k + q * 0.4), 1, 1);
    const bw = Math.max(3, Math.round(8 * k)), bh = Math.max(2, Math.round(3 * k));
    R(dx - (bw >> 1), dy - bh - 2, bw, bh, '#1e1a1c'); R(dx + d * (bw >> 1) - (d < 0 ? 2 : 0), dy - bh - 4, 3, 3, '#1e1a1c'); R(dx - d * (bw >> 1), dy - bh - 3, 1, 2, '#1e1a1c');
    for (const [lx, ph] of [[-(bw >> 1) + 1, 0], [-1, 1], [(bw >> 1) - 2, 0]]) R(dx + lx, dy - 2, 1, 2 - ((st + ph) & 1), '#1e1a1c');
  }]);
}
function drawSpinner(t, DL) {
  if (t < SPIN.a || t >= SPIN.off) return;
  let x, y, mode;
  if (t < SPIN.at) { const u = smooth(SPIN.a, SPIN.at, t); [x, y] = [lerp(TRAIL[1][0], OVAL.cx, u), lerp(TRAIL[1][1], 201, u)]; mode = 'in'; }
  else if (t < SPIN.b) { x = OVAL.cx; y = 201; mode = 'spin'; }
  else { const u = (t - SPIN.b) / (SPIN.off - SPIN.b); [x, y] = trailPoint(Math.max(0, u * 1.4 - 0.4)); if (u < 0.3) { x = lerp(OVAL.cx, TRAIL[0][0], u / 0.3); y = lerp(201, TRAIL[0][1], u / 0.3); } mode = 'out'; }
  const h = personH(y), P = sized(P_SPIN, h), fr = step(t, 400) & 1;
  if (h < 5) return;
  DL.push([y, () => {
    if (mode === 'spin') {
      const s = Math.min(1, (t - SPIN.at) / 3, (SPIN.b - t) / 3), q = Math.floor(t * (2 + 6 * s)) % 4;   // slow, faster, slow
      const view = [1, 2, 0, 2][q], dir = q === 3 ? -1 : 1, arms = s > 0.6 ? 6 : 3;
      reflectOf(x, y, () => person(x, y, P, { view, dir, aL: arms, aR: arms }));
      shadow(x, y, 8); person(x, y, P, { view, dir, aL: arms, aR: arms });
    } else {
      const dir = mode === 'in' ? -1 : 1;
      if (inOval(Math.round(x), Math.round(y) + 2)) reflectOf(x, y, () => skater(x, y, P, dir, fr, 'swing')); else shadow(x, y, 6);
      if (h >= 9) skater(x, y, P, dir, fr, 'swing'); else tiny(x, y, h, P.top, fr);
    }
  }]);
}
function drawShinny(t) {
  const goal = GOALS.find(g => t >= g - 2 && t < g + 4);
  let puck = null;
  SHINNY.forEach((s, i) => {
    const x = s.cx + wave(t, s.k, s.ph) * 14, y = RINK.y0 + 6 + Math.round(wave(t, s.k + 3, s.ph + 0.3) * 4) + (i & 1) * 2, h = personH(y);
    const cheer = goal && t >= goal && (i & 1) === 0;
    const P = SHINNY_P[i];
    person(x, y, P, { view: 2, dir: wave(t, s.k, s.ph + 0.25) > 0 ? 1 : -1, step: step(t, 480) & 1, aR: cheer ? 6 : 3 });
    ctx.fillStyle = '#c8a070';                                          // the stick, blade on the ice ahead
    if (cheer) ctx.fillRect(Math.round(x) + 2, Math.round(y) - h - 3, 1, 4);
    else { ctx.fillRect(Math.round(x) + 2, Math.round(y) - 3, 1, 2); ctx.fillRect(Math.round(x) + 3, Math.round(y) - 1, 2, 1); }
    if (i === 0) puck = [x + 5, y];
  });
  if (goal && t >= goal - 2 && t < goal) puck = [lerp(puck[0], RINK.x0 + 5, (t - goal + 2) / 2), lerp(puck[1], 174, (t - goal + 2) / 2)];
  else if (goal) puck = [RINK.x0 + 5, 174];
  R(Math.round(puck[0]), Math.round(puck[1]), 1, 1, '#0e0e14');
}
function drawCast(t, crowd) {
  const DL = [];
  const spinning = t >= SPIN.at && t < SPIN.b + 4, clap = t >= SPIN.b && t < SPIN.b + 4;
  for (const c of STAY) if (c.rank < crowd) DL.push([c.y, () => {
    const o = { ...c.o };
    if (c.id === 'buyer') o.aR = frac(t / 12 + 0.3) < 0.4 ? 3 : 0;                       // reaches for the cup at the hatch
    if (c.id === 'sipper') o.aR = frac(t / 8) < 0.3 ? 4 : 0;                           // sips her cocoa
    if (c.id === 'mug') o.aR = frac(t / 10 + 0.5) < 0.3 ? 4 : 3;
    if (c.id === 'warm' && spinning) { o.dir = -1; o.aR = clap ? (step(t, 960) & 1 ? 6 : 3) : 0; }   // turns to watch the spin
    if (c.id === 'facing' && clap) { o.aL = step(t, 960) & 1 ? 6 : 3; o.aR = o.aL; }
    if (c.id === 'lacer') {                                                              // bends to lace a skate, then the other
      const ph = frac(t / 16);
      o.aL = ph < 0.4 ? 3 : 0; o.aR = ph >= 0.5 && ph < 0.9 ? 3 : 0; o.lean = ph < 0.9 ? 1 : 0;
    }
    shadow(c.x, c.y, 8);
    person(c.x, c.y + (c.id === 'lacer' && o.lean ? 1 : 0), c.P, o);
    if (c.id === 'lacer') { const fx = c.x + (frac(t / 16) < 0.45 ? -3 : 2); R(fx, c.y + 3, 3, 1, '#1e1a20'); R(fx - 1, c.y + 4, 4, 1, '#d8dce8'); }
  }]);
  if (crowd > 0) DL.push([CABIN.base + 0.5, () => {                                  // the vendor in the hatch
    ctx.save(); ctx.beginPath(); ctx.rect(CABIN.x0 + 30, CABIN.roof + 12, 40, 20); ctx.clip();
    const pour = frac(t / 12) < 0.35;
    person(46, CABIN.roof + 40, P_VENDOR, { view: 1, aR: pour ? 3 : 0, aL: !pour && frac(t / 12) < 0.55 ? 3 : 0 });
    ctx.restore();
    if (pour) for (let k = 0; k < 3; k++) halo(HL.breath, 52 + k, CABIN.roof + 25 - k * 2, 0.4);
  }]);
  for (const s of OVAL_SKATERS) if (s.rank < crowd) {
    if (spinning && !s.kid && s.rank === 0 && false) continue;
    const a = TAU * (s.n * t / LOOP + s.ph), x = OVAL.cx + Math.cos(a) * OVAL.rx * (s.kid ? 0.62 : 0.8), y = OVAL.cy + Math.sin(a) * OVAL.ry * 0.55;
    const h = Math.round(s.P.h * personH(y) / 24), P = sized(s.P, h), dir = Math.sin(a) > 0 ? 1 : -1, fr = (step(t, 400) + Math.round(s.ph * 8)) & 1;
    DL.push([y, () => { reflectOf(x, y, () => skater(x, y, P, dir, fr, s.arms)); skater(x, y, P, dir, fr, s.arms); }]);
  }
  for (const s of TRAIL_SKATERS) if (s.rank < crowd) {                               // up the trail and back, shrinking
    const { u } = cycle(t, s.n, s.ph), w = u < 0.5 ? u * 2 : 2 - u * 2, [x, y] = trailPoint(w), h = personH(y), dir = u < 0.5 ? 1 : -1;
    DL.push([y, () => { if (h >= 9) skater(x, y, sized(s.P, h), dir, step(t, 400) & 1, 'back'); else tiny(x, y, h, s.P.top, step(t, 400) & 1); }]);
  }
  if (crowd > 0) { drawSled(t, DL); drawDog(t, DL); }
  if (crowd > 0) drawSpinner(t, DL);
  DL.push([CABIN.base, () => ctx.drawImage(CABIN_NOW, 0, 0)]);
  DL.push([HUT.base, () => ctx.drawImage(HUT_C, 0, 0)]);
  DL.push([BENCH.y - 1, () => ctx.drawImage(NEAR_C, 0, 0)]);
  DL.sort((a, b) => a[0] - b[0]);
  for (const [, draw] of DL) draw();
  if (crowd > 0) for (const c of STAY) if (c.rank < crowd) breath(c.breath[0], c.breath[1], t, c.x * 0.013);
}
let CABIN_NOW;

/* ================= Winnipeg's own snow at three depths ================= */
const FLAKES = [
  { n: 300, nMin: 16, nMax: 24, size: 1, col: '#a8b0d8', lit: '#e8e4f0', a: 0.55, land: [60, HZ], sway: 3 },
  { n: 280, nMin: 26, nMax: 36, size: 1, col: '#ccd4f2', lit: '#ffe4c0', a: 0.75, land: [HZ - 4, 210], sway: 5 },
  { n: 140, nMin: 38, nMax: 54, size: 2, col: '#eef2ff', lit: '#fff0d8', a: 0.85, land: [214, H + 6], sway: 8 },
].map((L, d) => ({ ...L, flakes: Array.from({ length: L.n }, (_, i) => ({ n: L.nMin + Math.floor(hash(i, d, 71) * (L.nMax - L.nMin)), ph: hash(i, d, 72), s: i * 7 + d, k: 12 + Math.floor(hash(i, d, 75) * 24) })) }));
const SNOW_LIGHTS = [{ x: 52, y: 178, r: 40 }, { x: 418, y: 182, r: 30 }, { x: BARREL.x, y: BARREL.y - 14, r: 20, col: '#ffc890' },
  { x: MU.tx, y: 56, r: 30, col: '#fff0c8' }, { x: BR.px, y: 118, r: 32, col: '#fff6e8' }, ...LANTERNS.map(L => ({ x: L.x + 1, y: L.y - L.h + 1, r: 12 }))];
const SNOW_LIGHTS_BB = [...SNOW_LIGHTS, { x: BB.x + (BB.w >> 1), y: BB.y + 8, r: 30, col: '#ffc8a8' }];
const litCol = (x, y, lights) => { for (const l of lights) if ((x - l.x) ** 2 + (y - l.y) ** 2 < l.r * l.r) return l.col || null; return undefined; };
function drawSnow(depth, t, lights) {
  const L = FLAKES[depth];
  ctx.globalAlpha = L.a;
  for (const f of L.flakes) {
    const { cyc, u } = cycle(t, f.n, f.ph);
    const land = lerp(L.land[0], L.land[1], hash(f.s, cyc, 73));
    const x0 = hash(f.s, cyc, 74) * (W + 60) - 30, y = Math.round(lerp(-6, land, u));
    const x = Math.round(x0 + Math.sin(TAU * (f.k * t / LOOP) + f.s) * L.sway * 0.5 - u * L.sway);
    const lc = litCol(x, y, lights);
    ctx.fillStyle = lc === undefined ? L.col : lc || L.lit;
    ctx.fillRect(x, y, L.size, L.size);
  }
  ctx.globalAlpha = 1;
}
const WEATHER = weatherPlans(['snow']);
const toWx = list => list.map(l => ({ x: l.x, y: l.y, rx: l.r, ry: l.r, col: l.col }));
const WX = { land: [[90, HZ], [HZ, 207], [H + 4, H + 4]], lights: toWx(SNOW_LIGHTS), leaves: { wind: [0, 20, W, 200] } };
const WX_BB = { ...WX, lights: toWx(SNOW_LIGHTS_BB) };

/* ================= The moving parts ================= */
// a train on the rail viaduct behind the Forks, glimpsed between the trees: lit coaches, or a freight's dark cars
function drawTrain(t) {
  for (const T of TRAINS) {
    if (t < T.a || t >= T.b) continue;
    const u = (t - T.a) / (T.b - T.a), head = T.dir > 0 ? lerp(-14, W + T.len + 14, u) : lerp(W + 14, -T.len - 14, u), y = 141;
    const carLen = T.freight ? 14 : 20;
    for (let k = 0; k < T.len; k++) {
      const x = Math.round(head - T.dir * k);
      if (x < 0 || x >= W) continue;
      const car = Math.floor(k / carLen), inCar = k % carLen;
      if (inCar === carLen - 1) continue;                                   // the gap between cars
      if (car === 0) { R(x, y - 1, 1, 6, T.freight ? '#2a2a32' : '#4a4e62'); R(x, y + 2, 1, 1, T.freight ? '#c86a2a' : '#8a8ea4'); }
      else if (T.freight) { R(x, y, 1, 1, hash(car, 5) < 0.5 ? '#b8c0dc' : '#3a3036'); R(x, y + 1, 1, 4, hash(car, 6) < 0.5 ? '#3a2a28' : '#2a303e'); }
      else { R(x, y, 1, 1, '#8a8ea4'); R(x, y + 1, 1, 4, '#4a4e62'); if (inCar % 3 === 1) R(x, y + 2, 1, 1, '#ffd890'); }
    }
    const hx = Math.round(head);
    if (hx > -4 && hx < W + 4) { R(hx, y + 1, 1, 1, '#fff6d8'); halo(HL.lamp, hx + T.dir, y + 1, 1); halo(HL.beam, hx + T.dir * 10, y + 2, 0.5); }
  }
}
// light pillars: columns of light standing over the city's lamps in the cold air, a calm timed event
function drawPillars(t) {
  const e = env(t, PILLARS.a, PILLARS.b, PILLARS.f);
  if (e <= 0) return;
  ctx.globalCompositeOperation = 'lighter';
  PILLARS.xs.forEach(([x, h], k) => {
    ctx.globalAlpha = e * (0.62 + 0.12 * wave(t, 8 + k, k * 0.3)) * (0.7 + 0.3 * smooth(PILLARS.a + k * 2, PILLARS.a + k * 2 + 10, t));
    ctx.drawImage(PILLAR_C[k], x - 2, 140 - h);
  });
  ctx.globalAlpha = 1; ctx.globalCompositeOperation = 'source-over';
}
// the Tower of Hope's light climbing from its foot to the lantern, which flares and settles
function drawClimb(t) {
  if (t < CLIMB.a || t >= CLIMB.b) return;
  const yc = Math.round(lerp(MU.roof - 3, 40, smooth(CLIMB.a, CLIMB.top, t))), e = env(t, CLIMB.a, CLIMB.b, 3);
  ctx.globalAlpha = 0.75 * e * (1 - smooth(CLIMB.top - 1, CLIMB.top + 2, t));
  for (let y = yc; y < yc + 3; y++) for (const [L, Rr] of towerSpans(y)) R(L, y, Rr - L, 1, GOLD.hi);
  ctx.globalAlpha = 1;
  halo(HL.lantern, MU.tx + 1, yc + 1, 0.6 * e);
  halo(HL.tower, MU.tx + 2, 48, 0.9 * env(t, CLIMB.top - 2, CLIMB.b, 3));
}
// cars crossing the Provencher Bridge behind the Esplanade: headlights one end, tail lights the other
const CARS = Array.from({ length: 7 }, (_, i) => ({ n: 5 + i * 2, ph: hash(i, 1, 61), east: i & 1 }));
function drawTraffic(t) {
  const span = W - BR.x0 + 40;
  for (const c of CARS) {
    const u = frac(c.n * t / LOOP + c.ph), x = Math.round(c.east ? BR.x0 - 10 + u * span : W + 20 - u * span);
    if (x < BR.x0 + 6 || x > W - 3) continue;
    const y = deckY(x) + 3;
    R(x, y, 3, 1, '#1c1c28'); R(c.east ? x + 2 : x, y, 1, 1, '#fff0c8'); R(c.east ? x : x + 2, y, 1, 1, '#ff3a2a');
    halo(HL.car, c.east ? x + 3 : x - 1, y, 0.5);
  }
}
// people walking the Esplanade Riel's deck, tiny at this distance
const DECK_WALKERS = [{ n: 2, ph: 0.1, d: 1, col: '#c84a3a' }, { n: 3, ph: 0.55, d: -1, col: '#3a6aa8' }, { n: 2, ph: 0.8, d: -1, col: '#e8c040' }, { n: 1, ph: 0.3, d: 1, col: '#8a8a9a' }];
function drawDeckWalkers(t) {
  for (const w of DECK_WALKERS) {
    const u = frac(w.n * t / LOOP + w.ph), x = Math.round(w.d > 0 ? lerp(BR.x0 + 6, W + 4, u) : lerp(W + 4, BR.x0 + 6, u));
    if (x >= W || Math.abs(x - BR.px) < 23) continue;
    const y = deckY(x) - 3;
    R(x, y - 3, 1, 1, '#e8c8a8'); R(x, y - 2, 1, 2, w.col); R(x + ((step(t, 768) + x) & 1), y, 1, 1, '#20202c');
  }
}
// pigeons startled off the Cathedral by its bells, flying over the viewer and away across the river
function drawPigeons(t) {
  if (t < PIGEONS.a || t >= PIGEONS.b) return;
  for (let k = 0; k < 7; k++) {
    const u = (t - PIGEONS.a - k * 0.4) / (PIGEONS.b - PIGEONS.a - 3);
    if (u < 0 || u > 1) continue;
    const e = Math.pow(u, 0.7), x = Math.round(lerp(476 - (k % 3) * 10, 70 + k * 22, e)), y = Math.round(lerp(236 - k * 5, 34 + (k % 4) * 10, e) + Math.sin(u * 9 + k) * 2);
    const s = Math.max(1, Math.round(lerp(4, 1, e))), up = (step(t, 1440) + k) & 1, col = '#a8acc4', lit = '#e8dcc8';   // grey birds lit from under by the city
    R(x, y, s + 1, Math.max(1, s >> 1), col); R(x, y + Math.max(1, s >> 1) - 1, s + 1, 1, lit);
    R(x - s, up ? y - s : y + 1, s, 1, col); R(x + s + 1, up ? y - s : y + 1, s, 1, col);
  }
}
// snow lifted off the ice by the wind in thin streams, and glints where the lamps catch the fresh snow
const DRIFTS = Array.from({ length: 44 }, (_, i) => ({ y: 160 + Math.floor(hash(i, 1, 81) * 86), n: 3 + Math.floor(hash(i, 2, 81) * 4), ph: hash(i, 3, 81), len: 6 + Math.floor(hash(i, 4, 81) * 14) }));
function drawDrifts(t) {
  for (let i = 0; i < DRIFTS.length; i++) {
    const d = DRIFTS[i], { cyc, u } = cycle(t, d.n, d.ph), y = d.y + Math.round(hash(i, cyc, 82) * 4), k = (y - 152) / 60;
    const x = Math.round(lerp(-30, W + 10, u)), len = Math.max(3, Math.round(d.len * k)), a = Math.sin(Math.PI * u) * 0.26;
    ctx.globalAlpha = a; R(x, y, len, 1, '#dfe6fa'); if (len > 5) R(x + 2, y - 1, len - 4, 1, '#c8d0ec');
  }
  ctx.globalAlpha = 1;
}
const GLINTS = [];
{
  const r = rng(77);
  while (GLINTS.length < 640) {
    const x = Math.floor(r() * W), y = 160 + Math.floor(r() * 108);
    if (inOval(x, y) || (x < CABIN.x1 + 2 && y < CABIN.base + 2)) continue;
    GLINTS.push({ x, y, w: warmAt(x, y) });
  }
}
function drawGlints(t) {
  const s = step(t, 1200);
  for (let i = 0; i < GLINTS.length; i++) {
    if (hash(i, s, 43) > 0.18) continue;
    const g = GLINTS[i];
    ctx.globalAlpha = 0.3 + 0.4 * Math.min(1, g.w + (g.y - 160) / 200); R(g.x, g.y, 1, 1, g.w > 0.15 ? '#fff4d8' : '#e8eeff');
  }
  ctx.globalAlpha = 1;
}
function drawFire(t) {                                      // flames over the barrel's rim, sparks rising, the glow
  const s = step(t, 1440), f = hash(1, s, 2);
  for (let k = 0; k < 6; k++) R(BARREL.x - 4 + k + (hash(k, s, 3) < 0.5 ? 0 : 1) - 1, BARREL.y - 12 - Math.round(hash(k, s, 4) * 4), 1, 2 + (k & 1), k & 1 ? '#ffd060' : '#ff8a30');
  for (let j = 0; j < 5; j++) {
    const { cyc, u } = cycle(t, 90, j / 5);
    if (u < 0.6) R(Math.round(BARREL.x - 3 + hash(j, cyc, 5) * 6 + u * 5), Math.round(BARREL.y - 14 - u * 22), 1, 1, u < 0.3 ? '#ffd890' : '#ff9a40');
  }
  halo(HL.fire, BARREL.x, BARREL.y - 14, 0.7 + 0.3 * f);
}
function drawSmoke(t) {                                     // wood smoke from the cabin's stovepipe, warm steam from the hut's crown
  for (let j = 0; j < 10; j++) {
    const { u } = cycle(t, 16, j / 10), r = Math.min(6, 1 + Math.floor(u * 7));
    halo(HL.steam[r], CABIN.x0 + 81 + u * 30 + Math.sin(TAU * (u * 1.4 + 0.2)) * 3, CABIN.roof - 20 - u * 36, 0.75 * (1 - u) * smooth(0, 0.08, u));
  }
  for (let j = 0; j < 6; j++) {
    const { u } = cycle(t, 20, j / 6), r = Math.min(5, 1 + Math.floor(u * 6));
    halo(HL.steam[r], (HUT.x0 + HUT.x1) / 2 + u * 18 + Math.sin(TAU * (u + j * 0.2)) * 2, HUT.top - 2 - u * 26, 0.35 * (1 - u) * smooth(0, 0.1, u));
  }
}
const WINS_ON = (w, t) => hash(w.id, Math.floor(frac(t / LOOP + w.ph) * w.n), 13) < 0.5;
function drawWins(t) { for (const w of WINS) if (WINS_ON(w, t)) R(w.x, w.y, 1, 1, WARMW[1]); }
function drawBillboard(t, pitch) {
  if (!PITCH.covers(t, pitch)) ctx.drawImage(BB_AD, BB.x, BB.y);
  PITCH.draw(ctx, t, BB.x, BB.y, pitch);
  halo(HL.sign, BB.x + (BB.w >> 1), BB.y + 4, 0.7);
  for (const lx of [BB.x + 14, BB.x + BB.w - 11]) halo(HL.bulb, lx, BB.y - 4, 0.9);
}

/* ================= Pre-render everything static ================= */
let HL, SKY_C, MOON_C, WISP_C, CITY_C, MU_C, BANK_C, BRIDGE_C, ICE_C, HUT_C, CABIN_C, CABIN_BARE, NEAR_C, BB_AD, PITCH, VIG_C,
  SPRUCE_C, MIRROR_SRC, PILLAR_C;
function renderVignette() {
  const p = painter(W, H);
  for (let y = 0; y < H; y++) for (let x = 0; x < W; x++) {
    const d = Math.hypot((x - W / 2) / (W * 0.62), (y - H * 0.45) / (H * 0.7));
    if (d > 0.84 && (d - 0.84) * 3 > bayer(x, y)) p.put(x, y, '#05060e', 90);
  }
  return p.done();
}
const renderPillar = h => ditherFill(5, h, ['#000000', '#2a2418', '#5a4e38', '#9a8a64', '#d8c898'], (i, j) =>
  (1 - Math.abs(i - 2) / 2.7) * Math.pow(j / h, 0.9) * (1 - Math.pow(j / h, 8) * 0.5));
function prerender() {
  WINS.length = 0; SPRUCES.length = 0; RIME.length = 0;
  HL = {
    moon: makeHalo(40, '#9ab0ff', 0.2, 1.8), moonCore: makeHalo(14, '#fff2d0', 0.3, 1.5),
    tower: makeHalo(46, '#ffd890', 0.16, 1.5), lantern: makeHalo(16, '#fff2c8', 0.4, 1.4), roots: makeHalo(40, '#ffc880', 0.08, 1.4),
    pylon: makeHalo(40, '#fff4e0', 0.14, 1.5), resto: makeHalo(28, '#ffd890', 0.28, 1.5), lamp: makeHalo(6, '#ffe0a0', 0.5, 1.3), prov: makeHalo(5, '#ffb860', 0.45, 1.3),
    hatch: makeHalo(50, '#ffb060', 0.3, 1.5), hut: makeHalo(34, '#ffb060', 0.28, 1.5), fire: makeHalo(26, '#ff9a40', 0.36, 1.5), bulb: makeHalo(4, '#ffc860', 0.45, 1.2),
    sign: makeHalo(30, '#ff9070', 0.14, 1.5), breath: makeHalo(3, '#e8ecff', 0.5, 1.2), beam: makeHalo(12, '#fff0c8', 0.18, 1.2), car: makeHalo(3, '#fff0c8', 0.4, 1.2),
    steam: [0, 1, 2, 3, 4, 5, 6].map(r => makeHalo(r + 1, '#c8d0e4', 0.45, 0.7)),
  };
  SKY_C = renderSky(); MOON_C = renderMoon(); WISP_C = WISPS.map(renderWisp); CITY_C = renderCity(); MU_C = renderMuseum();
  BANK_C = renderBank(); BRIDGE_C = renderBridges(); ICE_C = renderIce(); HUT_C = renderHut();
  CABIN_C = renderCabin(true); CABIN_BARE = renderCabin(false); NEAR_C = renderNearRight(); BB_AD = renderBillboardAd();
  PITCH = makePitch(BB.w, BB.h, { accent: '#88aaff' });
  PILLAR_C = PILLARS.xs.map(([, h]) => renderPillar(h));
  { const [c, x] = makeCanvas(W, H); const put = (i, y, col) => { x.fillStyle = col; x.fillRect(Math.round(i), Math.round(y), 1, 1); };
    for (const s of SPRUCES) drawSpruce(put, s.x, s.y, s.h, s.lean); SPRUCE_C = c; }
  VIG_C = renderVignette();
  const [m, mx] = makeCanvas(W, H);                        // the mirror's source: the static frame above the ice
  for (const c of [SKY_C, CITY_C, MU_C, BANK_C, BRIDGE_C]) mx.drawImage(c, 0, 0);
  MIRROR_SRC = m;
}

/* ================= Frame compositor (back to front) ================= */
function renderFrame(t, { pitch = false, billboard = true, weather, reducedMotion = false, crowd = CROWD } = {}) {
  t = ((t % LOOP) + LOOP) % LOOP;
  ctx.setTransform(1, 0, 0, 1, 0, 0); ctx.globalAlpha = 1; ctx.globalCompositeOperation = 'source-over';
  const wx = WEATHER(weather), wxc = billboard ? WX_BB : WX, lights = billboard ? SNOW_LIGHTS_BB : SNOW_LIGHTS;
  const dry = !wx.shared || wx.shared === 'snow' || wx.shared === 'leaves';
  // the sky: stars, the moon, the snow cloud drifting, light pillars over downtown
  ctx.drawImage(SKY_C, 0, 0);
  ctx.fillStyle = '#e8eeff';
  for (const s of STARS) {
    ctx.globalAlpha = s.b * (0.4 + 0.6 * wave(t, s.k, s.ph));
    ctx.fillRect(s.x, s.y, 1, 1);
    if (s.big) { ctx.globalAlpha *= 0.4; ctx.fillRect(s.x - 1, s.y, 3, 1); ctx.fillRect(s.x, s.y - 1, 1, 3); }
  }
  ctx.globalAlpha = 1;
  halo(HL.moon, MOON.x, MOON.y); halo(HL.moonCore, MOON.x, MOON.y);
  ctx.drawImage(MOON_C, MOON.x - MOON.r, MOON.y - MOON.r);
  for (const L of WISP_C) { const off = Math.floor(frac(L.k * t / LOOP) * W); ctx.drawImage(L.c, off - W, L.y); ctx.drawImage(L.c, off, L.y); }
  drawPillars(t);
  // downtown, the train behind the Forks, the far snow
  ctx.drawImage(CITY_C, 0, 0);
  drawWins(t);
  drawTrain(t);
  if (wx.snow) drawSnow(0, t, lights);
  drawWeather(ctx, wx.shared, 0, t, wxc, reducedMotion);
  // the Museum: the tower's glow, the building, the lantern, the light climbing
  halo(HL.tower, MU.tx + 2, 70, 0.85); halo(HL.roots, 152, 128, 0.8);
  ctx.drawImage(MU_C, 0, 0);
  halo(HL.lantern, MU.tx + 1, 45, 0.45 + 0.08 * wave(t, 12));
  drawClimb(t);
  BANDS.slice(0, 3).forEach((bd, k) => {                   // the city's light gliding slowly along the bands' top edges
    const u = frac(t * (3 + k) / LOOP + k * 0.37), x0 = Math.round(lerp(bd[2], bd[4], u));
    ctx.fillStyle = GLASS.hi;
    for (let i = 0; i < 7; i++) if (x0 + i <= bd[4]) { ctx.globalAlpha = 0.7 * Math.sin(Math.PI * (i + 0.5) / 7); ctx.fillRect(x0 + i, bd[0] + 1, 1, 1); }
    ctx.globalAlpha = 1;
  });
  ctx.drawImage(BANK_C, 0, 0);
  { const st = step(t, 960); ctx.fillStyle = '#f4f8ff';
    for (let i = 0; i < RIME.length; i++) if (hash(i, st, 44) < 0.12) { ctx.globalAlpha = 0.5 + 0.5 * hash(i, st, 45); ctx.fillRect(RIME[i].x, RIME[i].y, 1, 1); }
    ctx.globalAlpha = 1; }
  // the bridges, their lamps, the traffic and the walkers
  halo(HL.pylon, BR.px, 110, 0.9);
  ctx.drawImage(BRIDGE_C, 0, 0);
  drawTraffic(t);
  drawDeckWalkers(t);
  halo(HL.resto, BR.px, 128, 0.9);
  for (const lx of DECK_LAMPS) halo(HL.lamp, lx + 2, deckY(lx) - 8, 0.9);
  for (const lx of PROV_LAMPS) { R(lx, deckY(lx) - 5, 1, 1, '#ffc070'); halo(HL.prov, lx, deckY(lx) - 5, 0.8); }
  // the frozen river: the cleared oval's soft mirror, streaks under the lights, spruces, lanterns, the far patch
  ctx.drawImage(ICE_C, 0, 0);
  drawMirror(t);
  for (const [sx, w, col, a] of [[MU.tx + 2, 6, '#ffe0a0', 0.16], [BR.px, 5, '#fff2e0', 0.14], [54, 12, '#ffc070', 0.18], [418, 10, '#ffb860', 0.16]])
    streak(ctx, t, sx, w, col, a, sx * 0.21, ovalTop(sx) + 2, Math.round(OVAL.cy + ovalHalf(sx) * 0.8));
  ctx.drawImage(SPRUCE_C, 0, 0);
  for (const L of LANTERNS) {
    const ly = L.y - L.h + 1, fl = 0.85 + 0.15 * hash(L.x, step(t, 600), 7);
    streak(ctx, t, L.x + 1, 3, '#ffd890', 0.3, L.x * 0.3, L.y + 3, Math.round(OVAL.cy + ovalHalf(L.x) * 0.7));
    R(L.x, ly, 2, 2, '#ffe8b0'); halo(HL.lamp, L.x + 1, ly + 1, fl); halo(HL.lantern, L.x + 1, ly + 1, 0.22 * fl);
  }
  halo(HL.bulb, FAR_HUT.x, FAR_HUT.base - 2, 0.9); halo(HL.lamp, FAR_HUT.x, FAR_HUT.base - 3, 0.5);
  if (crowd > 0) drawShinny(t);
  if (dry) { drawDrifts(t); drawGlints(t); }
  if (wx.snow) drawSnow(1, t, lights);
  drawWeather(ctx, wx.shared, 1, t, wxc, reducedMotion);
  // the near corner: the cabin, the hut, the bench and the barrel in the cast's feet order, the fire, the smoke
  halo(HL.hut, 418, 186, 0.8 + 0.1 * wave(t, 30));
  CABIN_NOW = billboard ? CABIN_C : CABIN_BARE;
  drawCast(t, crowd);
  drawFire(t);
  halo(HL.hatch, 54, 178, 0.9);
  drawSmoke(t);
  if (billboard) drawBillboard(t, pitch);
  drawPigeons(t);
  if (wx.snow) drawSnow(2, t, lights);
  drawWeather(ctx, wx.shared, 2, t, wxc, reducedMotion);
  ctx.drawImage(VIG_C, 0, 0);
}

const CROWD = 0.5;
export default {
  id: 'winnipeg',
  name: 'Winnipeg',
  tagline: 'Skating the frozen Forks',
  timeZone: 'America/Winnipeg',
  accent: '#88aaff',
  focusX: 226,
  landmark: [MU.x0, BR.px + 28],                      // the Museum and the Esplanade Riel's pylon with its restaurant
  landmarkTop: BR.tip,                                // the pylon's tip
  billboard: BB,
  crowd: CROWD,
  spots: [                                            // what answers a tap (site/js/ui/spots.js)
    { kind: 'landmark', x: 117, y: MU.tip, w: 17, h: MU.roof - MU.tip },              // the Tower of Hope
    { kind: 'landmark', x: BR.px - 4, y: BR.tip, w: 9, h: 90 },                       // the Esplanade Riel's pylon
    { kind: 'window', x: 48, y: CABIN.roof + 12, w: 26, h: 20 },                       // the cabin's hatch
  ],
  ambience: { wind: 0.32, crowd: 0.1, traffic: 0.06 },
  events: [
    { t: TRAINS[0].a, type: 'train', dur: TRAINS[0].b - TRAINS[0].a, pan: [-1, 1], gain: 0.22 },
    { t: TRAINS[1].a, type: 'train', dur: TRAINS[1].b - TRAINS[1].a, pan: [1, -1], gain: 0.3 },
    { t: CLIMB.top + 1, type: 'chime', x: MU.tx, gain: 0.35 },
    { t: SPIN.at, type: 'chime', x: OVAL.cx, gain: 0.22 }, { t: SPIN.b, type: 'chime', x: OVAL.cx, gain: 0.3 },
    ...BELLS.map(t => ({ t, type: 'bell', x: 330, gain: 0.35 })),
  ],
  create(c) {
    ctx = c;
    prerender();
    return { render: renderFrame };
  },
};
